import type { ChatProvider } from '@xsai-ext/shared-providers'
import type { CommonContentPart, Message, SystemMessage } from '@xsai/shared-chat'

import type { ProviderConfig } from '../stores/backgroundActivity'
import type { StreamEvent } from '../stores/llm'
import type { ChatToolContext } from '../tools/chat-actions'
import type { ChatAssistantMessage, ChatMessage, ChatSlices, TimestampedSystemMessage } from '../types/chat'
import type { CallHandlerContext } from './chat-call-handler'
import type { MessageDispatchContext, SystemNotificationContext } from './chat-message-dispatcher'
import type { PostProcessContext } from './chat-post-processors'

import { useBroadcastChannel, useDocumentVisibility, useLocalStorage } from '@vueuse/core'
import { defineStore, storeToRefs } from 'pinia'
import { computed, nextTick, ref, toRaw, watch } from 'vue'

import { useLlmmarkerParser } from '../composables/llmmarkerParser'
import { useEnhancedCall } from '../composables/useEnhancedCall'
import { useEnhancedPrompt } from '../composables/useEnhancedPrompt'
import { buildGroupChatSystemPrompt } from '../composables/useGroupChatPrompt'
import { useOfflineMode } from '../composables/useOfflineMode'
import { CHAT_STORAGE_DEBOUNCE } from '../constants/performance'
import { AI_FIRST_RESPONSE_TIMEOUT_MS, AI_STREAM_IDLE_TIMEOUT_MS } from '../constants/timeout'
import { ApiPriority, useApiPriorityQueueStore } from '../stores/api-priority-queue'
import { useBackgroundActivityStore } from '../stores/backgroundActivity'
import { useCallStore } from '../stores/call'
import { useContactsStore } from '../stores/contacts'
import { useLLM } from '../stores/llm'
import { useMemoryStore } from '../stores/memory'
import { usePluginsStore } from '../stores/plugins'
import { usePostsStore } from '../stores/posts'
import { useRelationshipStore } from '../stores/relationship'
import { useUserPersonaStore } from '../stores/user-persona'
import { CHAT_ACTION_TOOL_NAMES, getToolDisplayName } from '../tools/chat-actions'
import { buildAttachedFileBlock } from '../utils/attached-file'
import { compressAvatarForPush } from '../utils/avatarCompression'
import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { collectPatContextMessages, removeConsumedPatContextMessages } from '../utils/pat-message'
import { createQueue } from '../utils/queue'
import { LEAK_PLACEHOLDER } from '../utils/response-leak-guard'
import { stripBracketDescriptionsFromResponse } from '../utils/strip-bracket-descriptions'
import { isPromptLayerSystemMessage, isUiOnlySystemMessage } from '../utils/system-message-types'
import { TTS_FLUSH_INSTRUCTION } from '../utils/tts'
import { getNonVisionModelCacheKey, hasImageUrlMessage, isNonVisionModelCached, isVisionUnsupportedError, markNonVisionModelCached, pruneExpiredNonVisionModelCache, sanitizeMessagesForNonVisionModel } from '../utils/vision'
import { handleAIInitiatedCall, handleAnswerCall, handleCallDecision, handleCallHangUp, handleCallTranscript, handleSpeakInCall } from './chat-call-handler'
import { compressLargeDataUrlsInMessages, IMAGE_PLACEHOLDER_TEXT, isImageDecodeError, isValidImageDataUrl, stripAllImagePartsFromMessages } from './chat-helpers'
import { dispatchFallbackMessage, dispatchMultipleMessages, dispatchSingleMessage, insertSystemNotification, parseMessagesArray } from './chat-message-dispatcher'
import { handleVoiceMessageSend, runAllPostProcessors } from './chat-post-processors'
import { useAiriCardStore } from './modules'
import { useSettings } from './settings'

export interface ErrorMessage {
  role: 'error'
  content: string
}

export interface ExportedChatData {
  messagesByContact: Record<string, Array<ChatMessage | ErrorMessage>>
  conversationTurnsByContact: Record<string, number>
  lastMessageTimeByContact: Record<string, number>
}

interface AutoReplyOptions {
  providerId: string
  model: string
  getProvider: () => Promise<ChatProvider>
  providerConfig?: Record<string, unknown>
}

// 📡 BroadcastChannel 事件类型定义
type BroadcastChannelEvents
  = | { type: 'new_message', data: { message: ChatMessage | ErrorMessage, contactId?: string } }
    | { type: 'messages_updated', data: { timestamp: number } }
    | { type: 'clear_chat', data: { timestamp: number, contactId?: string } }
    | { type: 'ai_typing', data: { isTyping: boolean } }

export const useChatStore = defineStore('chat', () => {
  const { stream, discoverToolsCompatibility } = useLLM()
  const { systemPrompt } = storeToRefs(useAiriCardStore())
  const airiCardStore = useAiriCardStore()

  // ✨ 集成增强型系统
  const memoryStore = useMemoryStore()
  const callStore = useCallStore()
  const contactsStore = useContactsStore()
  const bgActivityStore = useBackgroundActivityStore()
  const apiQueueStore = useApiPriorityQueueStore()
  const postsStore = usePostsStore()
  const pluginsStore = usePluginsStore()
  const personaStore = useUserPersonaStore()
  const relationshipStore = useRelationshipStore()
  const { isOfflineMode } = useOfflineMode()

  const { activeContactId } = storeToRefs(contactsStore)

  const DEFAULT_CONTACT_KEY = '__global__'
  const nonVisionModelCache = useLocalStorage('chat/non-vision-model-cache', {} as Record<string, { markedAt: number }>)
  nonVisionModelCache.value = pruneExpiredNonVisionModelCache(nonVisionModelCache.value)

  // 📡 跨页面通信 - BroadcastChannel
  const { post: broadcastMessage, data: receivedBroadcast } = useBroadcastChannel<
    BroadcastChannelEvents,
    BroadcastChannelEvents
  >({ name: 'meowchat-messages' })

  // 🔔 通知权限状态
  const notificationPermission = ref<NotificationPermission>('default')

  // 🔔 通知开关（用户可手动关闭，独立于浏览器权限）
  const notificationEnabled = useLocalStorage('chat/notification-enabled', false)

  // 🔔 初始化通知权限状态
  if ('Notification' in window) {
    notificationPermission.value = Notification.permission

    // 监听浏览器权限变化（用户通过浏览器设置修改权限时自动同步）
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then((status) => {
        status.addEventListener('change', () => {
          notificationPermission.value = Notification.permission
        })
      }).catch(() => {})
    }
  }
  // 预留 relationshipStore 给未来的关系调整功能
  const {
    buildEnhancedSystemPrompt,
    extractLastUserMessage,
    processAIResponse,
    injectTimestampsToMessages,
  } = useEnhancedPrompt()
  const {
    detectCallIntent,
    handleAICallDecision,
    detectHangUpIntent,
    handleAIHangUp,
  } = useEnhancedCall()

  const sending = ref(false)

  // 上次调用是否失败
  const lastCallFailed = ref(false)

  // 防止内联摘要并发重复触发：记录正在生成中的联系人 key
  // Set 内有 key 时跳过本轮触发，生成完成（成功或失败）后移除
  const inlineSummaryGenerating = new Set<string>()

  // ⏱️ AI回复防抖定时器
  let aiReplyDebounceTimer: number | null = null
  let pendingAutoReplyOptions: AutoReplyOptions | null = null

  const onBeforeMessageComposedHooks = ref<Array<(message: string) => Promise<void>>>([])
  const onAfterMessageComposedHooks = ref<Array<(message: string) => Promise<void>>>([])
  const onBeforeSendHooks = ref<Array<(message: string) => Promise<void>>>([])
  const onAfterSendHooks = ref<Array<(message: string) => Promise<void>>>([])
  const onTokenLiteralHooks = ref<Array<(literal: string) => Promise<void>>>([])
  const onTokenSpecialHooks = ref<Array<(special: string) => Promise<void>>>([])
  const onStreamEndHooks = ref<Array<() => Promise<void>>>([])
  const onAssistantResponseEndHooks = ref<Array<(message: string) => Promise<void>>>([])
  // 🆕 完整文本生成 hook（用于通话开场白、通话动作等需要整段语音的场景）
  const onFullTextGeneratedHooks = ref<Array<(payload: { text: string, isVoice?: boolean }) => Promise<void>>>([])

  function onBeforeMessageComposed(cb: (message: string) => Promise<void>) {
    onBeforeMessageComposedHooks.value.push(cb)
  }

  function onAfterMessageComposed(cb: (message: string) => Promise<void>) {
    onAfterMessageComposedHooks.value.push(cb)
  }

  function onBeforeSend(cb: (message: string) => Promise<void>) {
    onBeforeSendHooks.value.push(cb)
  }

  function onAfterSend(cb: (message: string) => Promise<void>) {
    onAfterSendHooks.value.push(cb)
  }

  function onTokenLiteral(cb: (literal: string) => Promise<void>) {
    onTokenLiteralHooks.value.push(cb)
  }

  function onTokenSpecial(cb: (special: string) => Promise<void>) {
    onTokenSpecialHooks.value.push(cb)
  }

  function onStreamEnd(cb: () => Promise<void>) {
    onStreamEndHooks.value.push(cb)
  }

  function onAssistantResponseEnd(cb: (message: string) => Promise<void>) {
    onAssistantResponseEndHooks.value.push(cb)
  }

  // 🆕 完整文本生成回调（用于一次性生成整段语音）
  function onFullTextGenerated(cb: (payload: { text: string, isVoice?: boolean }) => Promise<void>) {
    onFullTextGeneratedHooks.value.push(cb)
  }

  async function triggerFullTextSpeech(text: string, isVoice = false) {
    if (onFullTextGeneratedHooks.value.length === 0) {
      callStore.releasePendingAssistantMessageByText(text)
      return
    }

    try {
      for (const hook of onFullTextGeneratedHooks.value) {
        await hook({ text, isVoice })
      }
    }
    catch (ttsError) {
      console.warn('完整文本TTS处理失败，继续', ttsError)
      callStore.releasePendingAssistantMessageByText(text)
    }
  }

  function clearHooks() {
    onBeforeMessageComposedHooks.value = []
    onAfterMessageComposedHooks.value = []
    onBeforeSendHooks.value = []
    onAfterSendHooks.value = []
    onTokenLiteralHooks.value = []
    onTokenSpecialHooks.value = []
    onStreamEndHooks.value = []
    onAssistantResponseEndHooks.value = []
    onFullTextGeneratedHooks.value = []
  }

  // 聊天气泡是纯文本渲染（不解析 Markdown），因此这里明确禁止 Markdown/代码块，
  // 约束的是"给用户看的消息文本"须为纯文本，降低模型在 text 字段里吐 ```代码块``` 导致
  // 气泡"掉格式"。结构化动作全部通过工具调用完成，正文不再要求 JSON。
  const codeBlockSystemPrompt = '- The visible message text you send to the user must be plain conversational text, like in a chat app: no Markdown, no code fences (```), no inline backticks, no headings, no tables, and never paste raw JSON into it.\n'
  const mathSyntaxSystemPrompt = '- Write math and symbols as plain text (e.g. x^3), not LaTeX.\n'

  /**
   * 🔔 请求通知权限
   */
  async function requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('❌ 浏览器不支持通知功能')
      return false
    }

    if (Notification.permission === 'granted') {
      notificationPermission.value = 'granted'
      return true
    }

    if (Notification.permission === 'denied') {
      console.warn('❌ 通知权限被拒绝')
      notificationPermission.value = 'denied'
      return false
    }

    try {
      const permission = await Notification.requestPermission()
      notificationPermission.value = permission
      console.info(`🔔 通知权限状态: ${permission}`)
      return permission === 'granted'
    }
    catch (error) {
      console.error('❌ 请求通知权限失败:', error)
      return false
    }
  }

  /**
   * 🔔 发送桌面通知
   *
   * @param message - 要通知的消息对象
   * @param message.content - 消息内容
   * @param message.type - 消息类型
   * @param message.slices - 消息切片
   * @param options - 通知选项
   * @param options.skipUserMessage - 是否跳过用户消息
   * @param options.characterName - 角色名称
   * @param options.characterAvatar - 角色头像
   */
  async function sendDesktopNotification(
    message: {
      content: string
      type?: string
      slices?: ChatSlices[]
    },
    options?: {
      skipUserMessage?: boolean
      characterName?: string
      characterAvatar?: string
    },
  ) {
    // 0. 检查用户是否启用了通知
    if (!notificationEnabled.value) {
      console.debug('🔕 用户已关闭通知')
      return
    }

    // 1. 检查通知权限
    if (!('Notification' in window)) {
      console.info('🔕 浏览器不支持通知')
      return
    }

    if (Notification.permission !== 'granted') {
      console.info('🔕 通知权限未授予')
      return
    }

    // 2. 在通话中不发送通知（避免干扰通话体验）
    if (callStore.isCallActive) {
      console.info('🔕 通话中，跳过通知')
      return
    }

    // 2.5 拉黑过滤：被双向 blocked 的联系人不应该触发通知
    // 但 pending_user_approval（AI 申请加回）需要通知，让用户感知到申请
    const activeContactId = contactsStore.activeContactId
    if (activeContactId && contactsStore.isFullyBlocked(activeContactId)) {
      console.info('🔕 当前联系人已拉黑，跳过通知')
      return
    }

    // 只在页面不可见（用户切换到其他页面/标签/应用）时才发送通知
    // 这样用户在聊天界面时不会被通知打扰，离开时才会收到提醒
    if (document.visibilityState === 'visible') {
      console.info('🔕 页面可见，用户正在查看，跳过通知')
      return
    }

    // 4. 准备通知内容
    const characterName = options?.characterName || airiCardStore.activeCard?.name || 'Pluto'
    const characterAvatar = options?.characterAvatar || contactsStore.activeContact?.avatar || '/logo.png'

    let notificationBody = ''
    let hasContent = false

    if (message.slices && message.slices.length > 0) {
      // 遍历 slices 提取文本内容
      for (const slice of message.slices) {
        if (slice.type === 'text' && slice.text) {
          notificationBody += slice.text
          hasContent = true
        }
      }
    }

    // 如果没有从 slices 提取到内容，使用 content
    if (!hasContent && message.content) {
      if (typeof message.content === 'string') {
        notificationBody = message.content
        hasContent = true
      }
    }

    // 处理特殊消息类型的显示
    const messageType = message.type || 'text'
    if (messageType === 'sticker') {
      notificationBody = '[表情]'
      hasContent = true
    }
    else if (messageType === 'voice_message') {
      notificationBody = '[语音消息]'
      hasContent = true
    }
    else if (messageType === 'image_url' || messageType === 'text_photo' || messageType === 'simulated_image') {
      notificationBody = '[图片]'
      hasContent = true
    }

    if (!hasContent) {
      console.info('🔕 消息无内容，跳过通知')
      return
    }

    // 截断过长的内容
    if (notificationBody.length > 100) {
      notificationBody = `${notificationBody.substring(0, 97)}...`
    }

    // 6. 创建并显示通知（使用 SW showNotification 兼容 iOS PWA）
    try {
      const notificationOptions: NotificationOptions = {
        body: notificationBody,
        icon: typeof characterAvatar === 'string' ? characterAvatar : undefined,
        badge: '/logo.png',
        tag: `meowchat-message-${Date.now()}`, // 使用时间戳避免覆盖
        requireInteraction: false, // 不强制用户交互，3-5秒后自动消失
        silent: false, // 播放系统通知音
      }

      // 优先使用 Service Worker 的 showNotification（iOS PWA 不支持 new Notification()）
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        await registration.showNotification(`${characterName} 发来一条消息`, notificationOptions)
      }
      else {
        // 降级：非 SW 环境使用 Notification 构造函数
        const notification = new Notification(`${characterName} 发来一条消息`, notificationOptions)
        notification.onclick = () => {
          window.focus()
          notification.close()
        }
      }

      console.info(`🔔 已发送桌面通知: ${characterName} - ${notificationBody.substring(0, 30)}...`)
    }
    catch (error) {
      console.error('❌ 发送桌面通知失败:', error)
    }
  }

  // ✨ 使用 IndexedDB 存储代替 localStorage，支持更大的存储容量
  // 聊天记录可能包含 base64 图片附件，数据量很大，localStorage 的 5-10MB 限制容易被突破
  const messagesByContactStorage = useIndexedDBStorage<Record<string, Array<ChatMessage | ErrorMessage>>>('chat/messages-by-contact', {}, {
    debounceMs: CHAT_STORAGE_DEBOUNCE,
    deepWatch: false, // 禁用深度监听，聊天记录可能非常大，deep watch 每次变更都要遍历整棵树
  })
  const messagesByContact = messagesByContactStorage.data

  /** 通知聊天记录已变更（用于 push/splice 等原地变更后触发持久化） */
  function notifyMessagesChanged() {
    messagesByContactStorage.notifyChange()
  }

  const conversationTurnsByContactStorage = useIndexedDBStorage<Record<string, number>>('chat/conversation-turns-map', {})
  const conversationTurnsByContact = conversationTurnsByContactStorage.data

  const lastMessageTimeByContactStorage = useIndexedDBStorage<Record<string, number>>('chat/last-message-time-map', {})
  const lastMessageTimeByContact = lastMessageTimeByContactStorage.data

  // 等待所有聊天存储加载完成后再执行初始化逻辑
  const chatStorageLoaded = ref(false)
  Promise.all([
    waitForStorage(messagesByContactStorage),
    waitForStorage(conversationTurnsByContactStorage),
    waitForStorage(lastMessageTimeByContactStorage),
  ]).then(() => {
    chatStorageLoaded.value = true
    // 数据加载完成后执行旧版数据迁移
    migrateLegacyChatStorage()
    // 确保当前联系人会话存在
    ensureContactSession()
    console.debug('✅ 聊天存储加载完成（IndexedDB）')
  })

  // 🛡️ 页面关闭/切后台时强制保存，防止移动端 PWA 被系统杀死时丢失未保存的消息
  if (typeof window !== 'undefined') {
    const flushPendingSaves = () => {
      if (chatStorageLoaded.value) {
        messagesByContactStorage.save()
        conversationTurnsByContactStorage.save()
        lastMessageTimeByContactStorage.save()
      }
    }
    window.addEventListener('pagehide', flushPendingSaves)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushPendingSaves()
      }
    })
  }

  function getContactKey(contactId: string | null | undefined = activeContactId.value) {
    return contactId ?? DEFAULT_CONTACT_KEY
  }

  function cloneChatMessage(message: ChatMessage | ErrorMessage): ChatMessage | ErrorMessage {
    const raw = toRaw(message)
    if (raw.role === 'assistant') {
      const { textStream: _textStream, ...rest } = raw
      return structuredClone(rest) as ChatMessage | ErrorMessage
    }

    return structuredClone(raw) as ChatMessage | ErrorMessage
  }

  function normalizeImportedChatMessage(
    message: ChatMessage | ErrorMessage,
    contactIdMap?: Map<string, string>,
  ): ChatMessage | ErrorMessage {
    const cloned = cloneChatMessage(message) as (ChatMessage | ErrorMessage) & {
      senderId?: string
      senderName?: string
    }

    if ('senderId' in cloned && cloned.senderId) {
      const mappedSenderId = contactIdMap?.get(cloned.senderId) || cloned.senderId
      cloned.senderId = mappedSenderId

      const mappedSender = contactsStore.getContact(mappedSenderId)
      if (mappedSender?.name) {
        cloned.senderName = mappedSender.name
      }
    }

    return cloned
  }

  function buildChatMergeKey(message: ChatMessage | ErrorMessage): string {
    const timestamp = 'timestamp' in message && typeof message.timestamp === 'number'
      ? String(message.timestamp)
      : ''
    const senderId = 'senderId' in message && typeof message.senderId === 'string'
      ? message.senderId
      : ''
    const content = Array.isArray(message.content)
      ? JSON.stringify(message.content)
      : String(message.content ?? '')
    const simulatedImages = Array.isArray((message as any).simulatedImages)
      ? JSON.stringify((message as any).simulatedImages)
      : ''

    return [message.role, timestamp, senderId, content, simulatedImages].join('\x00')
  }

  function resolveImportedContactKey(
    sourceKey: string,
    contactIdMap?: Map<string, string>,
  ): string | null {
    if (sourceKey === DEFAULT_CONTACT_KEY) {
      return DEFAULT_CONTACT_KEY
    }

    const normalizedContactId = sourceKey.startsWith('contact_')
      ? sourceKey.slice('contact_'.length)
      : sourceKey

    if (contactIdMap && contactIdMap.size > 0) {
      return contactIdMap.get(normalizedContactId) || null
    }

    return getContactKey(normalizedContactId)
  }

  function exportChatData(contactIds?: string[]): ExportedChatData {
    const sourceKeys = Array.from(new Set([
      ...Object.keys(messagesByContact.value),
      ...Object.keys(conversationTurnsByContact.value),
      ...Object.keys(lastMessageTimeByContact.value),
    ])).sort((a, b) => Number(a.startsWith('contact_')) - Number(b.startsWith('contact_')))
    const selectedContactIds = contactIds?.length
      ? new Set(contactIds)
      : null

    const exportedData: ExportedChatData = {
      messagesByContact: {},
      conversationTurnsByContact: {},
      lastMessageTimeByContact: {},
    }

    for (const sourceKey of sourceKeys) {
      const targetKey = sourceKey === DEFAULT_CONTACT_KEY
        ? DEFAULT_CONTACT_KEY
        : sourceKey.startsWith('contact_')
          ? sourceKey.slice('contact_'.length)
          : sourceKey

      if (selectedContactIds && !selectedContactIds.has(targetKey)) {
        continue
      }

      const messages = messagesByContact.value[sourceKey]
      if (messages && !exportedData.messagesByContact[targetKey]) {
        exportedData.messagesByContact[targetKey] = messages.map(cloneChatMessage)
      }

      const conversationTurns = conversationTurnsByContact.value[sourceKey]
      if (conversationTurns !== undefined) {
        exportedData.conversationTurnsByContact[targetKey] = Math.max(
          exportedData.conversationTurnsByContact[targetKey] ?? 0,
          conversationTurns,
        )
      }

      const lastMessageTime = lastMessageTimeByContact.value[sourceKey]
      if (lastMessageTime !== undefined) {
        exportedData.lastMessageTimeByContact[targetKey] = Math.max(
          exportedData.lastMessageTimeByContact[targetKey] ?? 0,
          lastMessageTime,
        )
      }
    }

    return exportedData
  }

  function importChatData(
    chatData: ExportedChatData | undefined,
    contactIdMap?: Map<string, string>,
  ): { chatCount: number, chatMessageCount: number } {
    if (!chatData) {
      return { chatCount: 0, chatMessageCount: 0 }
    }

    const sourceKeys = new Set([
      ...Object.keys(chatData.messagesByContact || {}),
      ...Object.keys(chatData.conversationTurnsByContact || {}),
      ...Object.keys(chatData.lastMessageTimeByContact || {}),
    ])

    if (sourceKeys.size === 0) {
      return { chatCount: 0, chatMessageCount: 0 }
    }

    const nextMessagesByContact = { ...messagesByContact.value }
    const nextConversationTurns = { ...conversationTurnsByContact.value }
    const nextLastMessageTime = { ...lastMessageTimeByContact.value }
    let chatCount = 0
    let chatMessageCount = 0

    for (const sourceKey of sourceKeys) {
      const targetKey = resolveImportedContactKey(sourceKey, contactIdMap)
      if (!targetKey) {
        continue
      }

      const importedMessages = (chatData.messagesByContact[sourceKey] || [])
        .map(message => normalizeImportedChatMessage(message, contactIdMap))
      const existingMessages = nextMessagesByContact[targetKey] || []
      // 保留所有 prompt 层 system（stable / semiStable / dynamic 三条），find 只能拿到第一条会丢失后两条
      const existingSystemMessages = existingMessages.filter(message => isPromptLayerSystemMessage(message))
      const existingNonSystemMessages = existingMessages.filter(message => !isPromptLayerSystemMessage(message))
      const importedSystemMessages = importedMessages.filter(message => isPromptLayerSystemMessage(message))
      const importedNonSystemMessages = importedMessages.filter(message => !isPromptLayerSystemMessage(message))

      const seenMessages = new Set(existingNonSystemMessages.map(buildChatMergeKey))
      const dedupedImportedMessages = importedNonSystemMessages.filter((message) => {
        const mergeKey = buildChatMergeKey(message)
        if (seenMessages.has(mergeKey)) {
          return false
        }
        seenMessages.add(mergeKey)
        return true
      })

      const mergedMessages = [
        ...(existingSystemMessages.length > 0 ? existingSystemMessages : importedSystemMessages),
        ...existingNonSystemMessages,
        ...dedupedImportedMessages,
      ]

      if (mergedMessages.length > 0) {
        nextMessagesByContact[targetKey] = mergedMessages
      }

      const importedTurns = chatData.conversationTurnsByContact[sourceKey]
      if (importedTurns !== undefined) {
        nextConversationTurns[targetKey] = Math.max(nextConversationTurns[targetKey] ?? 0, importedTurns)
      }

      const importedLastMessageTime = chatData.lastMessageTimeByContact[sourceKey]
      if (importedLastMessageTime !== undefined) {
        nextLastMessageTime[targetKey] = Math.max(nextLastMessageTime[targetKey] ?? 0, importedLastMessageTime)
      }

      const hasNewData = dedupedImportedMessages.length > 0
        || (importedTurns !== undefined && importedTurns > (nextConversationTurns[targetKey] ?? 0))
        || (importedLastMessageTime !== undefined && importedLastMessageTime > (nextLastMessageTime[targetKey] ?? 0))

      if (hasNewData) {
        chatCount++
      }
      chatMessageCount += dedupedImportedMessages.length
    }

    messagesByContact.value = nextMessagesByContact
    conversationTurnsByContact.value = nextConversationTurns
    lastMessageTimeByContact.value = nextLastMessageTime
    notifyMessagesChanged()

    return { chatCount, chatMessageCount }
  }

  function migrateLegacyChatStorage() {
    if (typeof window === 'undefined')
      return

    if (Object.keys(messagesByContact.value).length === 0) {
      const legacyMessagesRaw = window.localStorage.getItem('chat/messages')
      if (legacyMessagesRaw) {
        try {
          const parsed = JSON.parse(legacyMessagesRaw) as Array<ChatMessage | ErrorMessage>
          if (Array.isArray(parsed) && parsed.length > 0) {
            messagesByContact.value = {
              [getContactKey()]: parsed,
            }
          }
        }
        catch (error) {
          console.warn('❌ 迁移旧版聊天记录失败:', error)
        }
        finally {
          window.localStorage.removeItem('chat/messages')
        }
      }
    }

    if (Object.keys(conversationTurnsByContact.value).length === 0) {
      const legacyTurnsRaw = window.localStorage.getItem('chat/conversation-turns')
      if (legacyTurnsRaw) {
        const legacyTurns = Number(legacyTurnsRaw)
        if (!Number.isNaN(legacyTurns)) {
          conversationTurnsByContact.value = {
            [getContactKey()]: legacyTurns,
          }
        }
        window.localStorage.removeItem('chat/conversation-turns')
      }
    }

    if (Object.keys(lastMessageTimeByContact.value).length === 0) {
      const legacyLastMessageRaw = window.localStorage.getItem('chat/last-message-time')
      if (legacyLastMessageRaw) {
        const legacyLastMessage = Number(legacyLastMessageRaw)
        if (!Number.isNaN(legacyLastMessage)) {
          lastMessageTimeByContact.value = {
            [getContactKey()]: legacyLastMessage,
          }
        }
        window.localStorage.removeItem('chat/last-message-time')
      }
    }
  }

  function setMessagesForContact(contactKey: string, newMessages: Array<ChatMessage | ErrorMessage>) {
    messagesByContact.value[contactKey] = newMessages
    notifyMessagesChanged()
  }

  /**
   * 生成内联对话摘要（Operit 式滚动摘要）。
   *
   * 把传入的对话消息快照压缩成一段摘要文字，返回待插入的 ChatInlineSummaryMessage。
   * 调用方负责在触发时刻截取消息快照并决定插入位置，本函数不读写 store，避免异步竞态。
   *
   * @param model     当前使用的模型 ID
   * @param provider  当前使用的 provider 实例
   * @param toCompress 要压缩的 user/assistant 消息快照
   * @returns          摘要消息对象，失败时返回 null
   */
  async function generateInlineSummary(
    model: string,
    provider: unknown,
    toCompress: Message[],
    abortSignal?: AbortSignal,
  ): Promise<import('../types/chat').ChatInlineSummaryMessage | null> {
    if (toCompress.length < 2)
      return null

    const conversationText = toCompress
      .map((m) => {
        const roleLabel = m.role === 'user' ? 'User' : 'AI'
        const content = typeof m.content === 'string'
          ? m.content
          : ((m.content ?? []) as any[]).map((c: any) => ('text' in c ? c.text : '')).join(' ')
        // 每条消息截断到 500 字符，避免 prompt 过长
        return `[${roleLabel}]: ${content.slice(0, 500)}${content.length > 500 ? '...' : ''}`
      })
      .join('\n')

    const summaryPrompt = `You are a conversation history compressor. Compress the following conversation into a concise third-person summary (within 300 words). Preserve key events, character states, and emotional context so the next turn of conversation can continue naturally. Use the SAME LANGUAGE as the conversation above.

Conversation:
${conversationText}

Return only the summary text, no extra formatting.`

    let responseText = ''
    try {
      await new Promise<void>((resolve, reject) => {
        // 合并外部 abortSignal（超时）与内部状态，确保超时时 stream 连接被正确中止
        stream(model, provider as any, [
          { role: 'user', content: summaryPrompt },
        ], {
          onStreamEvent(event: any) {
            if (event.type === 'text-delta' && event.text) {
              responseText += event.text
            }
            else if (event.type === 'finish') {
              resolve()
            }
            else if (event.type === 'error') {
              reject(new Error(event.error))
            }
          },
          abortSignal,
        })
      })
    }
    catch (e) {
      console.warn('⚠️ 内联摘要生成失败:', e)
      return null
    }

    const content = responseText.trim()
    if (!content)
      return null

    return {
      role: 'summary',
      content,
      timestamp: Date.now(),
      isHidden: true,
      coveredCount: toCompress.length,
    }
  }

  function ensureContactSession(contactKey = getContactKey()) {
    const existing = messagesByContact.value[contactKey]

    if (!existing || existing.length === 0) {
      const initialMessages = generateInitialMessage([])
      setMessagesForContact(contactKey, initialMessages)
      return
    }

    if (existing[0]?.role !== 'system') {
      const updated = [...generateInitialMessage(existing), ...existing]
      setMessagesForContact(contactKey, updated)
    }
    // ⚡ 会话已存在且首条是 system message 时，不做任何额外工作
  }

  const messages = computed<Array<ChatMessage | ErrorMessage>>({
    get() {
      const key = getContactKey()
      // ⚡ 不再在 getter 中调用 ensureContactSession
      // ensureContactSession 已在 activeContactId watcher 和初始化时调用
      // 在 getter 中重复调用会导致每次读取 messages 都触发额外检查，造成切换联系人卡顿
      return messagesByContact.value[key] ?? []
    },
    set(newMessages) {
      const key = getContactKey()
      setMessagesForContact(key, newMessages)
    },
  })

  const conversationTurns = computed<number>({
    get() {
      const key = getContactKey()
      const turns = conversationTurnsByContact.value[key]
      if (turns === undefined) {
        conversationTurnsByContact.value = {
          ...conversationTurnsByContact.value,
          [key]: 0,
        }
        return 0
      }
      return turns
    },
    set(value) {
      const key = getContactKey()
      conversationTurnsByContact.value = {
        ...conversationTurnsByContact.value,
        [key]: value,
      }
    },
  })

  /** 按目标联系人增加已完成的会话轮数，避免异步回复期间切换联系人写错数据。 */
  function incrementConversationTurns(contactId: string | null = activeContactId.value) {
    const key = getContactKey(contactId ?? undefined)
    conversationTurnsByContact.value = {
      ...conversationTurnsByContact.value,
      [key]: (conversationTurnsByContact.value[key] || 0) + 1,
    }
  }

  const lastMessageTime = computed<number>({
    get() {
      const key = getContactKey()
      return lastMessageTimeByContact.value[key] ?? 0
    },
    set(value) {
      const key = getContactKey()
      lastMessageTimeByContact.value = {
        ...lastMessageTimeByContact.value,
        [key]: value,
      }
    },
  })

  /** 按目标联系人更新最后消息时间，避免异步回复期间切换联系人写错数据。 */
  function setLastMessageTimeForContact(contactId: string | null, timestamp: number) {
    const key = getContactKey(contactId ?? undefined)
    lastMessageTimeByContact.value = {
      ...lastMessageTimeByContact.value,
      [key]: timestamp,
    }
  }

  // ⚡ 联系人切换标志：防止 ensureContactSession 已刷新 system message 后，
  // systemPrompt watcher 在 nextTick 中再次触发 generateInitialMessage 导致二次渲染
  let contactSwitchInProgress = false

  watch(activeContactId, (contactId) => {
    contactSwitchInProgress = true
    ensureContactSession(getContactKey(contactId ?? undefined))
    // 🔧 同步记忆系统的当前联系人ID，确保记忆按联系人隔离
    memoryStore.setCurrentContact(contactId ?? null)
    // ⚡ 使用 setTimeout(0) 而非 nextTick 重置标志
    setTimeout(() => {
      contactSwitchInProgress = false
    }, 0)
  }, { immediate: true })

  /**
   * ✨ 修改：生成初始消息，集成增强型提示词（三层缓存结构）
   * Claude 模型：返回 2-3 条 system message（stable / semiStable? / dynamic）
   * 非 Claude 模型：在 llm.ts 中合并为一条，此处统一返回数组
   */
  function generateInitialMessage(history: Array<ChatMessage | ErrorMessage> = []): SystemMessage[] {
    // 获取当前激活的角色卡片
    const activeCard = airiCardStore.activeCard

    if (!activeCard) {
      // 降级到原始提示词（单条）
      return [{
        role: 'system',
        content: [codeBlockSystemPrompt, mathSyntaxSystemPrompt, systemPrompt.value].join(''),
      }]
    }

    const nonSystemHistory = history.filter(m => !isPromptLayerSystemMessage(m) && m.role !== 'error') as Message[]

    // ✨ 使用增强型提示词构建器（返回三层结构）
    const result = buildEnhancedSystemPrompt({
      userInput: '',
      characterCard: activeCard,
      lastMessageTime: lastMessageTime.value,
      conversationTurns: conversationTurns.value,
      messages: nonSystemHistory,
    })

    const msgs: SystemMessage[] = [
      { role: 'system', content: [codeBlockSystemPrompt, mathSyntaxSystemPrompt, '\n\n', result.stable].join('') },
    ]
    if (result.semiStable.trim()) {
      msgs.push({ role: 'system', content: result.semiStable })
    }
    if (result.dynamic.trim()) {
      msgs.push({ role: 'system', content: result.dynamic })
    }
    return msgs
  }

  // 初始化 messages
  if (messages.value.length === 0) {
    messages.value = generateInitialMessage([])
  }

  /**
   * ✨ 修改：清空消息并重置新状态
   */
  function cleanupMessages() {
    // 使用赋值触发 computed setter，确保 IndexedDB 持久化更新
    messages.value = generateInitialMessage([])
    notifyMessagesChanged()
    conversationTurns.value = 0
    lastMessageTime.value = 0
    lastCallFailed.value = false

    // 📡 广播清空事件到其他页面
    broadcastMessage({
      type: 'clear_chat',
      data: {
        timestamp: Date.now(),
        contactId: contactsStore.activeContact?.id,
      },
    })

    // F5：清空消息时同步重置 pending_* 状态（好友申请/反思流程）。
    // 这些 pending 状态依赖聊天历史中的 prompt 让 AI 决策，消息一清就不可能推进，必须连带重置。
    // 故意保留 blocked_by_user / blocked_by_ai：用户拉黑/被 AI 拉黑是需要冷静期的明确状态，
    // 不应该被"清空消息"这个操作意外解除（避免成为绕过冷静期的后门）。
    const activeContact = contactsStore.activeContact
    const blockStatus = activeContact?.blockStatus?.status
    if (
      activeContact
      && blockStatus
      && (blockStatus === 'pending_ai_approval' || blockStatus === 'pending_user_approval' || blockStatus === 'pending_system_reflection')
    ) {
      contactsStore.forceUnblockByUser(activeContact.id)
      console.info(`🧹 清空消息时同步重置联系人「${activeContact.name}」的 pending 状态（${blockStatus}）`)
    }
  }

  /**
   * F4：清理指定联系人历史中的 friend_request_application_prompt 系统消息
   * 用于状态离开 pending_ai_approval 时（accept / reject / 强制解除 / 沉默 reject 等场景），
   * 避免该 prompt 残留在 LLM 上下文里持续诱导 AI
   */
  function purgeApplicationPromptMessages(contactId: string) {
    const key = getContactKey(contactId)
    const list = messagesByContact.value[key]
    if (!list || list.length === 0)
      return

    const filtered = list.filter((msg) => {
      if (msg.role !== 'system')
        return true
      return (msg as any).metadata?.type !== 'friend_request_application_prompt'
    })

    const removed = list.length - filtered.length
    if (removed === 0)
      return

    // setMessagesForContact 内部已 notifyMessagesChanged。
    // 当前 active 联系人时 messages computed 会自动从新 list 取值，不需手动赋 messages.value
    setMessagesForContact(key, filtered)
    console.info(`🧹 已清理联系人 ${contactId} 的 ${removed} 条 friend_request_application_prompt 消息`)
  }

  /**
   * 🗑️ 删除指定联系人的所有聊天数据
   * @param contactId 联系人ID
   */
  function deleteContactChatData(contactId: string) {
    const keysToDelete = Array.from(new Set([
      getContactKey(contactId),
      `contact_${contactId}`,
    ]))
    let messagesChanged = false

    for (const key of keysToDelete) {
      if (key in messagesByContact.value) {
        const { [key]: _removed, ...rest } = messagesByContact.value
        messagesByContact.value = rest
        messagesChanged = true
      }

      if (key in conversationTurnsByContact.value) {
        const { [key]: _removed, ...rest } = conversationTurnsByContact.value
        conversationTurnsByContact.value = rest
      }

      if (key in lastMessageTimeByContact.value) {
        const { [key]: _removed, ...rest } = lastMessageTimeByContact.value
        lastMessageTimeByContact.value = rest
      }
    }

    if (messagesChanged) {
      notifyMessagesChanged()
    }
  }

  /**
   * ✨ 监听系统提示词变化，自动重新生成
   * 使用 nextTick 防抖：切换联系人时 activeCardId 和 activeContactId 会连续变化，
   * 导致 systemPrompt watcher 和 activeContactId watcher 都触发 generateInitialMessage。
   * 通过 nextTick 合并，确保只在所有同步状态更新完成后执行一次。
   */
  let systemPromptUpdatePending = false
  watch(systemPrompt, () => {
    if (systemPromptUpdatePending)
      return
    systemPromptUpdatePending = true
    nextTick(() => {
      systemPromptUpdatePending = false
      if (contactSwitchInProgress)
        return
      if (messages.value.length > 0 && messages.value[0].role === 'system') {
        const newSystemMsgs = generateInitialMessage(messages.value)
        const nonSystemMsgs = messages.value.filter(m => !isPromptLayerSystemMessage(m))
        messages.value = [...newSystemMsgs, ...nonSystemMsgs]
        notifyMessagesChanged()
      }
    })
  })

  // 定期自动更新系统提示词（每5轮对话）
  watch(conversationTurns, (newValue) => {
    if (newValue > 0 && newValue % 5 === 0) {
      const newSystemMsgs = generateInitialMessage(messages.value)
      const nonSystemMsgs = messages.value.filter(m => !isPromptLayerSystemMessage(m))
      messages.value = [...newSystemMsgs, ...nonSystemMsgs]
      notifyMessagesChanged()
    }
  })

  // 📡 监听跨页面消息广播
  watch(receivedBroadcast, (event) => {
    if (!event)
      return

    console.debug('📨 收到跨页面广播:', event.type)

    switch (event.type) {
      case 'new_message': {
        const newMsg = event.data.message
        const targetKey = getContactKey(event.data.contactId)
        const targetMessages = messagesByContact.value[targetKey] ?? []
        // 检查目标会话是否已存在（避免 BroadcastChannel 与 IndexedDB 恢复重复）。
        if (targetMessages.some(message => buildChatMergeKey(message) === buildChatMergeKey(newMsg))) {
          return
        }

        console.debug('✅ 从其他页面接收到新消息，已同步到当前页面')
        targetMessages.push(newMsg)
        messagesByContact.value[targetKey] = targetMessages
        notifyMessagesChanged()
        break
      }

      case 'messages_updated': {
        console.info('🔄 其他页面更新了消息历史')
        // LocalStorage 会自动同步，不需要手动处理
        break
      }

      case 'clear_chat': {
        const currentContactId = contactsStore.activeContact?.id
        if (event.data.contactId && event.data.contactId !== currentContactId) {
          console.info('⏭️  清空事件不属于当前联系人，跳过')
          return
        }

        console.info('🗑️  其他页面清空了聊天记录')
        // LocalStorage 会自动同步，不需要手动处理
        ensureContactSession()
        break
      }

      case 'ai_typing': {
        console.debug(event.data.isTyping ? '⌨️  AI 正在输入...' : '⏸️  AI 停止输入')
        // 可以在 UI 中显示"正在输入"指示器
        break
      }
    }
  })

  // 📱 监听页面可见性变化（处理移动端后台恢复）
  const visibility = useDocumentVisibility()
  watch(visibility, async (current, previous) => {
    // 从后台恢复到前台
    if (previous === 'hidden' && current === 'visible') {
      console.debug('👀 页面从后台恢复')

      // ⚠️ 关键：不要在这里打断 sending 状态！
      // 如果 AI 正在回复（sending = true），说明：
      // 1. 后台心跳保持了连接活跃
      // 2. 流式 API 请求可能仍在进行
      // 3. 应该让它自然完成，而不是强制中断

      if (sending.value) {
        console.debug('⏳ AI 正在回复中，保持等待状态')
        // 不做任何操作，让 API 请求自然完成
      }

      // 🎯 检查是否需要生成朋友圈（离线后回来）
      try {
        await bgActivityStore.checkAndRunMomentsSimulation()
      }
      catch (error) {
        console.error('❌ 朋友圈检查失败:', error)
      }

      // 可选：刷新 UI 以显示最新状态
      // 由于 messages 是响应式的，LocalStorage 的变化会自动同步
    }

    // 页面进入后台
    if (previous === 'visible' && current === 'hidden') {
      console.debug(`👋 页面进入后台（sending=${sending.value}）`)

      // 📝 记录状态用于调试
      if (sending.value) {
        console.debug('💬 AI 正在回复，后台心跳将保持连接活跃')
        // 短周期心跳（60秒）会帮助保持后台活跃 5-30 分钟
      }
    }
  })

  const streamingMessage = ref<ChatAssistantMessage>({ role: 'assistant', content: '', slices: [], tool_results: [] })

  // UI 临时提示（不入库，刷新即失效）：保留 transientNotices 供 MobileChatHistory 订阅展示；
  // 当前没有入口主动写入，如未来需要再补 addTransientNotice 即可
  interface TransientNotice {
    id: string
    text: string
    createdAt: number
  }
  const transientNotices = ref<TransientNotice[]>([])
  const transientNoticeTimers = new Set<ReturnType<typeof setTimeout>>()
  /** 清理所有未到期的临时提示定时器（store 销毁 / 测试 teardown 场景） */
  function clearTransientNoticeTimers() {
    for (const t of transientNoticeTimers)
      clearTimeout(t)
    transientNoticeTimers.clear()
    transientNotices.value = []
  }

  // HMR 场景：模块热替换会重新执行 defineStore 的 setup，旧实例的定时器不会被自动 dispose。
  // 这里显式在 dispose 钩子里清理，避免开发环境累积大量孤儿 setTimeout。
  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      clearTransientNoticeTimers()
    })
  }
  function safeHostname(url: string): string {
    try {
      const host = new URL(url).hostname
      if (host)
        return host
    }
    catch {
      // new URL 对协议相对 URL（//example.com/a.png）和裸路径会抛
    }
    try {
      // 协议相对 URL fallback：补一个 base 再解析
      const host = new URL(url, 'https://placeholder.invalid/').hostname
      if (host && host !== 'placeholder.invalid')
        return host
    }
    catch { /* ignore */ }
    return '未知来源'
  }

  // 🔧 记录当前正在发送消息的目标联系人 ID，防止切换联系人后流式气泡显示在错误的界面
  // 注意：依赖 apiQueueStore 的串行执行保证，不会出现并发 send 覆盖此值的情况
  const sendingContactId = ref<string | null>(null)

  /**
   * 构建一个精简的消息分发上下文，仅用于 insertSystemNotification
   * 复用 chat-message-dispatcher.ts 的统一实现
   */
  function buildMinimalDispatchCtx(): SystemNotificationContext {
    return {
      messages,
      notifyMessagesChanged,
      pushMessageToTarget: (msg: any) => {
        messages.value.push(msg)
        notifyMessagesChanged()
      },
    }
  }

  /**
   * 静默清理指定联系人会话里残留的损坏图片（发生 convert_request_failed 时兜底）。
   * - data: 开头但已不合法的 image_url part 直接移除
   * - data: 开头且不合法的 sticker 同规则移除
   * - 清空后的 content 补一个文本占位，避免 provider 对空 content 报错
   * 仅在确有改动时写回 store，避免无谓触发持久化/广播。
   * 使用显式 targetContactKey 而非 `messages.value`，防止异步过程中切换联系人把清理结果写到错误的会话。
   * @returns 本次清理掉的坏图 part 数量（含 image_url 与 data URL 表情包）
   */
  function purgeInvalidImagesFromContact(targetContactKey: string): number {
    const list = messagesByContact.value[targetContactKey]
    if (!list)
      return 0

    let removed = 0
    let mutated = false
    const nextList = list.map((msg) => {
      if (!Array.isArray(msg.content))
        return msg
      let msgChanged = false
      const nextContent = (msg.content as Array<{ type?: string, url?: string, image_url?: { url?: string } }>).flatMap((part) => {
        if (part?.type === 'image_url') {
          const u = part.image_url?.url
          if (typeof u !== 'string' || u.length === 0) {
            removed++
            msgChanged = true
            return []
          }
          if (u.startsWith('data:') && !isValidImageDataUrl(u)) {
            removed++
            msgChanged = true
            return []
          }
          return [part]
        }
        if (part?.type === 'sticker') {
          const u = part.url
          if (typeof u === 'string' && u.startsWith('data:') && !isValidImageDataUrl(u)) {
            removed++
            msgChanged = true
            return []
          }
          return [part]
        }
        return [part]
      })
      if (!msgChanged)
        return msg
      if (nextContent.length === 0) {
        const placeholder: CommonContentPart = { type: 'text', text: IMAGE_PLACEHOLDER_TEXT }
        nextContent.push(placeholder as unknown as { type?: string, url?: string, image_url?: { url?: string } })
      }
      mutated = true
      return { ...msg, content: nextContent } as typeof msg
    })

    if (!mutated)
      return 0

    messagesByContact.value = {
      ...messagesByContact.value,
      [targetContactKey]: nextList as Array<ChatMessage | ErrorMessage>,
    }
    notifyMessagesChanged()
    return removed
  }

  /**
   * 根据错误信息匹配并插入对应的错误通知到聊天列表
   */
  async function handleApiError(error: unknown, targetContext?: SystemNotificationContext): Promise<boolean> {
    const ctx = targetContext ?? buildMinimalDispatchCtx()

    try {
      const { matchApiError } = await import('../utils/api-error-matcher')
      const matched = matchApiError(error)
      if (matched) {
        insertSystemNotification(matched.message, matched.icon, ctx)
      }
    }
    catch (importError) {
      // 兜底：import 失败时使用简单错误提示
      const errorMessage = error instanceof Error ? error.message : String(error ?? '')
      const shortErr = errorMessage.length > 80 ? `${errorMessage.slice(0, 80)}…` : errorMessage
      insertSystemNotification(`AI 回复失败: ${shortErr}`, 'i-solar:danger-triangle-bold-duotone', ctx)
      console.error('[handleApiError] 导入错误匹配器失败:', importError)
    }

    return true
  }

  async function executeAutoReply(options: AutoReplyOptions) {
    try {
      const chatProvider = await options.getProvider()

      await send('', {
        providerId: options.providerId,
        model: options.model,
        chatProvider,
        providerConfig: options.providerConfig,
        skipUserMessage: true,
        countAsUserTurn: true,
        priority: ApiPriority.HIGH,
        requester: 'user_chat_debounced',
      })
    }
    catch (error) {
      // send() 内部已经调用了 handleApiError 插入通知，这里只记录日志，不重复插入
      console.error('❌ 自动触发AI回复失败:', error)
    }
  }

  /**
   * ⏱️ 使用防抖机制触发AI回复
   * 只有当输入框空闲特定秒数后才会真正触发AI
   */
  function triggerAIReplyWithDebounce(options: AutoReplyOptions) {
    // 清除之前的定时器
    if (aiReplyDebounceTimer !== null) {
      clearTimeout(aiReplyDebounceTimer)
    }

    // 手动模式：防抖延迟为 -1 时不自动触发，等待用户手动打包发送
    const settingsStoreRef = useSettings()
    const debounceDelay = settingsStoreRef.aiReplyDebounceDelay
    if (debounceDelay === -1) {
      console.info('⏱️ 手动打包模式，跳过自动触发AI回复')
      return
    }

    // 设置新的定时器
    const effectiveDelay = debounceDelay || 3000

    aiReplyDebounceTimer = window.setTimeout(async () => {
      console.info('⏱️ 防抖延迟结束，触发AI回复')
      aiReplyDebounceTimer = null

      if (sending.value) {
        console.info('⏳ AI 正在回复，延后执行本次自动回复')
        pendingAutoReplyOptions = options
        return
      }

      await executeAutoReply(options)
    }, effectiveDelay)

    console.info(`⏱️ 已设置AI回复防抖定时器 (${effectiveDelay}ms)`)
  }

  /**
   * 🚫 取消AI回复防抖
   * 当用户正在输入时调用
   */
  function cancelAIReplyDebounce() {
    if (aiReplyDebounceTimer !== null) {
      clearTimeout(aiReplyDebounceTimer)
      aiReplyDebounceTimer = null
      console.info('🚫 已取消AI回复防抖定时器')
    }
    // 同时清除待处理的自动回复，防止 AI 回复完成后误触发旧的自动回复
    if (pendingAutoReplyOptions !== null) {
      pendingAutoReplyOptions = null
      console.info('🚫 已清除待处理的自动回复')
    }
  }

  watch(sending, (isSending) => {
    if (!isSending && pendingAutoReplyOptions) {
      console.info('▶️ AI 回复完成，执行待处理的自动回复')
      const options = pendingAutoReplyOptions
      pendingAutoReplyOptions = null
      executeAutoReply(options).catch((err) => {
        console.error('❌ 待处理的自动回复执行失败:', err)
      })
    }
  })

  /**
   * 💬 只发送用户消息，不触发AI回复（配合防抖机制使用）
   */
  async function sendUserMessage(
    messageText: string,
    options: {
      attachments?: Array<
        | { type: 'image', data: string, mimeType: string }
        | { type: 'document', name: string, mimeType: string, text: string }
      >
      quote?: {
        timestamp: number
        content: string
        role: 'user' | 'assistant'
        senderName?: string
      }
    } = {},
  ) {
    if (!messageText && !options.attachments?.length)
      return

    // 🔌 调用插件的 onBeforeUserMessage 钩子
    const contactId = contactsStore.activeContactId || ''
    let processedMessageText = messageText
    let shouldBlock = false

    for (const handler of pluginsStore.beforeUserMessageHandlers) {
      try {
        const result = await handler(processedMessageText, {
          contactId,
          timestamp: Date.now(),
          role: 'user' as const,
        })

        if (result.modified && result.content) {
          processedMessageText = result.content
        }

        if (result.block) {
          shouldBlock = true
        }

        if (result.showToast) {
          // 显示提示消息（需要引入 toast 工具）
          console.info(`[Plugin Toast] ${result.showToast}`)
        }
      }
      catch (e) {
        console.error('插件 onBeforeUserMessage 钩子执行失败:', e)
      }
    }

    // 如果插件要求阻止发送，则直接返回
    if (shouldBlock) {
      return
    }

    // 如果消息被修改了，使用修改后的消息
    if (processedMessageText !== messageText) {
      messageText = processedMessageText
    }

    for (const hook of onBeforeMessageComposedHooks.value) {
      await hook(messageText)
    }

    // 文档附件以纯文本形式前置注入到 user message 的 text part 中，
    // 用 <attached-file> 标签让模型识别边界。原文整体 XML escape，
    // 防止文档里出现字面量 </attached-file> 截断块。
    const documentAttachments = options.attachments?.filter(a => a.type === 'document') ?? []
    const composedText = documentAttachments.length > 0
      ? `${documentAttachments
          .map(d => buildAttachedFileBlock({ name: d.name, mimeType: d.mimeType, size: d.text.length }, d.text))
          .join('\n\n')}\n\n${messageText}`.trim()
      : messageText

    const contentParts: CommonContentPart[] = [{ type: 'text', text: composedText }]

    if (options.attachments) {
      for (const attachment of options.attachments) {
        if (attachment.type === 'image') {
          contentParts.push({
            type: 'image_url',
            image_url: {
              url: `data:${attachment.mimeType};base64,${attachment.data}`,
            },
          })
        }
      }
    }

    const finalContent = contentParts.length > 1 ? contentParts : composedText

    const userMessage: any = {
      role: 'user' as const,
      content: finalContent,
      timestamp: Date.now(),
    }

    // 持久化结构化的文档元信息，UI 渲染时优先读这里而不是从正文里正则反推
    if (documentAttachments.length > 0) {
      userMessage.attachments = documentAttachments.map(d => ({
        type: 'document' as const,
        name: d.name,
        mimeType: d.mimeType,
        size: d.text.length,
      }))
    }

    // 如果有引用消息，添加到用户消息中
    if (options.quote) {
      userMessage.quote = {
        timestamp: options.quote.timestamp,
        content: options.quote.content,
        senderName: options.quote.senderName || (options.quote.role === 'user' ? '你' : 'AI'),
        role: options.quote.role,
      }
    }

    messages.value.push(userMessage)
    notifyMessagesChanged()

    // 📡 广播用户消息到其他页面
    broadcastMessage({
      type: 'new_message',
      data: {
        message: JSON.parse(JSON.stringify(toRaw(userMessage))),
        contactId: contactsStore.activeContact?.id,
      },
    })

    // 🔄 用户发送消息时，重置/启动后台活动倒计时
    const backgroundActivityStore = useBackgroundActivityStore()
    if (backgroundActivityStore.enabled) {
      if (backgroundActivityStore.isCountdownRunning) {
        backgroundActivityStore.resetCountdown()
      }
      else {
        // 倒计时未运行，首次启动后台活动引擎
        console.info('🚀 首次用户消息，自动启动后台活动引擎')
        backgroundActivityStore.start()
      }
    }

    for (const hook of onAfterMessageComposedHooks.value) {
      await hook(messageText)
    }

    // 🔌 调用插件的 onAfterUserMessage 钩子
    for (const handler of pluginsStore.afterUserMessageHandlers) {
      try {
        await handler(messageText, {
          contactId,
          timestamp: userMessage.timestamp,
          role: 'user' as const,
          messageId: String(userMessage.timestamp),
        })
      }
      catch (e) {
        console.error('插件 onAfterUserMessage 钩子执行失败:', e)
      }
    }
  }

  function setAiTypingState(isTyping: boolean) {
    broadcastMessage({
      type: 'ai_typing',
      data: { isTyping },
    })
  }

  async function send(
    sendingMessage: string,
    options: {
      providerId: string
      model: string
      chatProvider: ChatProvider
      providerConfig?: Record<string, unknown>
      attachments?: Array<
        | { type: 'image', data: string, mimeType: string }
        | { type: 'document', name: string, mimeType: string, text: string }
      >
      skipUserMessage?: boolean // 跳过添加用户消息，直接触发 AI 回复
      countAsUserTurn?: boolean // 用户消息已提前写入，但本次仍应计为正常用户轮次
      priority?: ApiPriority // 任务优先级
      requester?: string // 请求者标识（用于调试和取消）
      taskKind?: ChatToolContext['taskKind'] // 当前触发任务的专用响应协议
      quote?: { // 引用的消息（回复功能）
        timestamp: number
        content: string
        role: 'user' | 'assistant'
        senderName?: string
      }
    },
  ) {
    // 如果不跳过用户消息，则检查消息是否为空（在设置 sending 之前检查）
    if (!options.skipUserMessage && !sendingMessage && !options.attachments?.length)
      return

    // 💾 更新后台活动系统的 LLM 配置（用于朋友圈等功能）
    bgActivityStore.setSendOptions({
      providerId: options.providerId,
      model: options.model,
      chatProvider: options.chatProvider,
      providerConfig: options.providerConfig as ProviderConfig | undefined,
    })

    // 🚀 确保后台活动引擎已启动（首次发送消息时自动启动）
    if (bgActivityStore.enabled && !bgActivityStore.isCountdownRunning) {
      console.info('🚀 检测到后台活动引擎未运行，自动启动')
      bgActivityStore.start()
    }

    // 确定优先级：用户主动发送消息 = HIGH，后台活动 = LOW
    const priority = options.priority ?? (options.skipUserMessage ? ApiPriority.LOW : ApiPriority.HIGH)
    const requester = options.requester ?? (options.skipUserMessage ? 'background_activity' : 'user_chat')

    // 🔐 通过优先级队列执行任务
    return apiQueueStore.enqueue(async () => {
      // 🔧 捕获发送时的联系人 ID，防止异步过程中切换联系人导致消息推到错误的列表
      const targetContactId = activeContactId.value ?? null
      const targetContactKey = getContactKey(targetContactId ?? undefined)
      const pushMessageToTarget = (msg: any) => {
        const targetMessages = messagesByContact.value[targetContactKey]
        if (targetMessages)
          targetMessages.push(msg)
        else
          messagesByContact.value[targetContactKey] = [msg]
        notifyMessagesChanged()
      }
      const targetMessagesRef: { value: any[] } = {
        get value() {
          return messagesByContact.value[targetContactKey] ?? []
        },
        set value(value) {
          messagesByContact.value[targetContactKey] = value
        },
      }
      try {
        sending.value = true
        sendingContactId.value = targetContactId
        lastCallFailed.value = false

        // 🔑 关键：等待 Vue 更新 DOM，确保加载动画能够显示
        await nextTick()

        // 📡 显示"正在输入"状态（在API调用开始时）
        setAiTypingState(true)

        for (const hook of onBeforeMessageComposedHooks.value) {
          await hook(sendingMessage)
        }

        const documentAttachmentsForSend = options.attachments?.filter(a => a.type === 'document') ?? []
        const composedSendingMessage = documentAttachmentsForSend.length > 0
          ? `${documentAttachmentsForSend
              .map(d => buildAttachedFileBlock({ name: d.name, mimeType: d.mimeType, size: d.text.length }, d.text))
              .join('\n\n')}\n\n${sendingMessage}`.trim()
          : sendingMessage

        const contentParts: CommonContentPart[] = [{ type: 'text', text: composedSendingMessage }]

        if (options.attachments) {
          for (const attachment of options.attachments) {
            if (attachment.type === 'image') {
              contentParts.push({
                type: 'image_url',
                image_url: {
                  url: `data:${attachment.mimeType};base64,${attachment.data}`,
                },
              })
            }
          }
        }

        const finalContent = contentParts.length > 1 ? contentParts : composedSendingMessage

        // 只有在不跳过用户消息时才添加到历史记录
        if (!options.skipUserMessage) {
          const userMessage: any = {
            role: 'user' as const,
            content: finalContent,
            timestamp: Date.now(), // ⏰ 记录消息时间戳
          }

          // 持久化结构化的文档元信息，UI 渲染时优先读这里而不是从正文里正则反推
          if (documentAttachmentsForSend.length > 0) {
            userMessage.attachments = documentAttachmentsForSend.map(d => ({
              type: 'document' as const,
              name: d.name,
              mimeType: d.mimeType,
              size: d.text.length,
            }))
          }

          // 如果有引用消息，添加到用户消息中
          if (options.quote) {
            userMessage.quote = {
              timestamp: options.quote.timestamp,
              content: options.quote.content,
              senderName: options.quote.senderName || (options.quote.role === 'user' ? '你' : 'AI'),
              role: options.quote.role,
            }
          }

          targetMessagesRef.value.push(userMessage)
          notifyMessagesChanged()

          // 📡 广播用户消息到其他页面
          broadcastMessage({
            type: 'new_message',
            data: {
              message: JSON.parse(JSON.stringify(toRaw(userMessage))),
              contactId: targetContactId ?? contactsStore.activeContact?.id,
            },
          })
        }

        // 动态更新系统提示词；联系人切换不应改变本轮已锁定的角色卡。
        const activeContact = targetContactId
          ? contactsStore.getContact(targetContactId)
          : contactsStore.activeContact
        const activeCard = activeContact?.cardId
          ? airiCardStore.getCard(activeContact.cardId)
          : activeContact
            ? undefined
            : airiCardStore.activeCard
        // 确保人格存储已从 IndexedDB 加载完成，避免冷启动时 PART 1.5 / 群聊用户信息丢失
        await personaStore.waitForPersonaLoad()

        // 🆕 检测是否为群聊
        const isGroupChat = activeContact?.isGroup || false

        if (activeCard || isGroupChat) {
          let systemContent = ''

          // 群聊模式
          if (isGroupChat && activeContact) {
            console.info('🎭 检测到群聊模式，使用群聊专用System Prompt')

            // 获取所有成员信息
            const memberIds = activeContact.members || []
            const members = memberIds
              .map(id => contactsStore.getContact(id))
              .filter(Boolean) as any[]

            // 获取成员对应的角色卡
            const memberCards: Record<string, any> = {}
            for (const member of members) {
              if (member.cardId) {
                memberCards[member.cardId] = airiCardStore.cards[member.cardId]
              }
            }

            // 获取用户人格设定（优先使用绑定人格）
            const effectivePersona = personaStore.resolvePersonaForContact(activeContact)
            const userPersona = {
              name: effectivePersona?.name || '用户',
              persona: effectivePersona?.persona || '一个普通用户',
              gender: personaStore.getLocalizedGender(effectivePersona),
              birthday: effectivePersona?.birthday,
            }

            // 🆕 获取每个成员与用户的私聊历史
            const privateChatHistories: Record<string, ChatMessage[]> = {}
            for (const member of members) {
              const memberContactKey = getContactKey(member.id)
              const memberChatHistory = messagesByContact.value[memberContactKey] || []

              // 只保留有效的私聊消息
              const validMessages = memberChatHistory.filter(msg =>
                !('isHidden' in msg && (msg as any).isHidden) // 过滤隐藏消息
                && msg.role !== 'error', // 过滤错误消息
              ) as ChatMessage[]

              if (validMessages.length > 0) {
                privateChatHistories[member.id] = validMessages
                console.info(`📚 加载成员 ${member.name} 的私聊历史: ${validMessages.length} 条消息`)
              }
            }

            // 构建群聊System Prompt
            const groupPrompt = buildGroupChatSystemPrompt({
              groupContact: activeContact,
              members,
              memberCards,
              userPersona,
              currentTime: new Date().toLocaleString('zh-CN'),
              history: targetMessagesRef.value as any[],
              privateChatHistories, // 🆕 传入私聊历史
            })

            systemContent = groupPrompt
          }
          // 单聊模式（原有逻辑）
          else if (activeCard) {
            const userInput = extractLastUserMessage(targetMessagesRef.value as Message[])
            const result = buildEnhancedSystemPrompt({
              userInput,
              characterCard: activeCard,
              lastMessageTime: lastMessageTimeByContact.value[targetContactKey] ?? 0,
              conversationTurns: conversationTurnsByContact.value[targetContactKey] ?? 0,
              messages: targetMessagesRef.value.filter(m => !isPromptLayerSystemMessage(m)) as Message[],
            })

            const newSystemMsgs: SystemMessage[] = [
              { role: 'system', content: [codeBlockSystemPrompt, mathSyntaxSystemPrompt, '\n\n', result.stable].join('') },
            ]
            if (result.semiStable.trim()) {
              newSystemMsgs.push({ role: 'system', content: result.semiStable })
            }
            if (result.dynamic.trim()) {
              newSystemMsgs.push({ role: 'system', content: result.dynamic })
            }

            const nonSystemMsgs = targetMessagesRef.value.filter(m => !isPromptLayerSystemMessage(m))
            targetMessagesRef.value = [...newSystemMsgs, ...nonSystemMsgs]
            notifyMessagesChanged()
          }

          // 群聊模式：单条 system message
          if (isGroupChat) {
            targetMessagesRef.value[0] = {
              role: 'system',
              content: systemContent,
            }
            notifyMessagesChanged()
          }
        }

        // 📡 增强 prompt 构建完成后立即同步到后端（含完整 systemPrompt + 用户消息）
        // 防止 PWA 被杀时后端拿到旧上下文；system message 已更新为三层缓存结构
        syncToOfflineServer({ userReplied: true, contactId: targetContactId }).catch(() => {})

        const createParser = () => {
          // 普通文本仍然即时流式显示；仅在开头疑似旧版内部结构时短暂探测，
          // 避免破损的 actions JSON 在 processAIResponse 完成前泄漏到气泡或 TTS。
          let literalProbe = ''
          let probeResolved = false
          let suppressInternalStructure = false

          const emitLiteral = async (literal: string) => {
            for (const hook of onTokenLiteralHooks.value) {
              await hook(literal)
            }

            streamingMessage.value.content += literal

            // merge text slices for markdown
            const lastSlice = streamingMessage.value.slices.at(-1)
            if (lastSlice?.type === 'text') {
              lastSlice.text += literal
              return
            }

            streamingMessage.value.slices.push({
              type: 'text',
              text: literal,
            })
          }

          return useLlmmarkerParser({
            onLiteral: async (literal) => {
              if (suppressInternalStructure)
                return

              if (!probeResolved) {
                literalProbe += literal
                const trimmedProbe = literalProbe.trimStart()
                const looksLikeInternalPrefix = /^```(?:json)?\s*(?:\{|\[|$)/i.test(trimmedProbe)
                  || /^\{\s*"/.test(trimmedProbe)
                  || /^\[\s*\{/.test(trimmedProbe)
                if (looksLikeInternalPrefix) {
                  suppressInternalStructure = true
                  literalProbe = ''
                  return
                }

                // 非 JSON 开头立即放行；疑似以括号/代码围栏开头的普通文本最多延迟 256 字符。
                const couldBeInternalPrefix = /^(?:\{|\[|```)/.test(trimmedProbe)
                if (!couldBeInternalPrefix || literalProbe.length >= 256) {
                  probeResolved = true
                  const pendingLiteral = literalProbe
                  literalProbe = ''
                  await emitLiteral(pendingLiteral)
                }
                return
              }

              await emitLiteral(literal)
            },
            onSpecial: async (special) => {
              for (const hook of onTokenSpecialHooks.value) {
                await hook(special)
              }
            },
            minLiteralEmitLength: 24, // Avoid emitting literals too fast. This is a magic number and can be changed later.
          })
        }

        // 🆕 防重复处理标志
        let finishProcessed = false
        let parser = createParser()

        const toolCallQueue = createQueue<ChatSlices>({
          handlers: [
            async (ctx) => {
              if (ctx.data.type === 'tool-call') {
                streamingMessage.value.slices.push(ctx.data)
                return
              }

              if (ctx.data.type === 'tool-call-result') {
                streamingMessage.value.tool_results.push(ctx.data)
              }
            },
          ],
        })

        streamingMessage.value = { role: 'assistant', content: '', slices: [], tool_results: [] }

        // 检查是否是第一次互动（没有任何 assistant 消息）
        const isFirstInteraction = targetMessagesRef.value.filter(m => m.role === 'assistant').length === 0

        // ✨ 获取角色名称用于消息前缀
        const characterName = activeCard?.name || 'AI'
        const effectivePersona = personaStore.resolvePersonaForContact(activeContact)
        const userName = effectivePersona?.name || '你'

        // 当消息间隔超过 5 分钟时，会自动添加 [时间: YYYY年MM月DD日 HH:MM] 前缀
        // 撤回消息处理策略：
        // - 用户撤回的消息：从上下文中完全移除（AI 看不到原文）
        // - AI 自己撤回的消息：保留在上下文中并标注为已撤回（AI 记得自己说过什么）
        const activeMessages = (targetMessagesRef.value as any[])
          .filter((m: any) => {
            if (isUiOnlySystemMessage(m))
              return false
            // AI 撤回的消息保留，用户撤回的消息移除
            return !m.recalled || m.role === 'assistant'
          })
          .map((m: any) => {
            // 对 AI 自己撤回的消息，在内容前加标注
            if (m.recalled && m.role === 'assistant') {
              const originalContent = typeof m.content === 'string' ? m.content : ''
              return {
                ...m,
                content: `[你已撤回此消息，原文: "${originalContent}"] （注意：对方看到的是"你撤回了一条消息"的提示，但对方可能已经看到了原文。你记得自己说过这句话。）`,
              }
            }
            return m
          })
        // 记录本轮 prompt 实际纳入的 pat_context 对象引用。成功完成回复后按引用精准清理，
        // 请求期间新追加的 pat_context 不在此集合中，因此会保留给下一轮。
        const consumedPatContextMessages = new Set<object>(collectPatContextMessages(activeMessages))

        // 🐛 拉黑系统诊断日志：检查发给 LLM 的消息是否包含好友申请提示
        if (import.meta.env.DEV) {
          const systemMsgsInPrompt = activeMessages.filter((m: any) => m.role === 'system')
          const friendRequestSystemMsgs = systemMsgsInPrompt.filter((m: any) => {
            const content = typeof m.content === 'string' ? m.content : ''
            return content.includes('用户提交了好友申请') || content.includes('friend_request_response')
          })
          if (friendRequestSystemMsgs.length > 0) {
            console.info(`📨 [chat→LLM] 即将发送给 AI 的消息中包含 ${friendRequestSystemMsgs.length} 条好友申请提示`)
            friendRequestSystemMsgs.forEach((m: any, idx: number) => {
              const content = typeof m.content === 'string' ? m.content : JSON.stringify(m.content)
              console.info(`  └─ [${idx}] role=${m.role}, content="${content.substring(0, 200)}${content.length > 200 ? '...' : ''}"`)
            })
          }
          // 当前激活联系人的拉黑状态
          const blockStatus = activeContact?.blockStatus
          if (blockStatus) {
            console.info('🚫 [chat→LLM] 当前激活联系人的拉黑状态:', blockStatus)
          }
        }
        const messagesWithTimestamps = injectTimestampsToMessages(
          activeMessages as Message[],
          activeContact?.timeAwarenessEnabled !== false,
        )

        // ⭐ Operit 式滚动摘要上下文构建：
        // - 若历史中存在内联摘要（role: 'summary'），只取「最后一条摘要 + 其后的对话消息」，
        //   摘要之前的旧消息全部从上下文剔除（它们已被压缩进摘要），但仍保留在 store 里供 UI 回看。
        // - 若还没有任何摘要，退回旧的 slice(-maxCtx) 兜底截断，避免首轮长对话 token 爆炸。
        // - system 消息（PART 0 记忆 / 角色卡等）始终全量保留。
        const settingsStore = useSettings()
        const maxCtx = settingsStore.maxContextMessages
        let truncatedMessages = messagesWithTimestamps

        const systemMessages = messagesWithTimestamps.filter(m => m.role === 'system')
        const nonSystemMessages = messagesWithTimestamps.filter(m => m.role !== 'system')
        const lastSummaryIdx = nonSystemMessages.findLastIndex(m => (m as any).role === 'summary')

        if (lastSummaryIdx !== -1) {
          // 有摘要：保留摘要本身 + 其后的对话消息。
          // 若摘要后又累积了超过 maxCtx 条（如批量导入历史），对「摘要之后」部分再兜底截断，
          // 避免 fromSummary 无上限撑爆 context window（摘要消息永远保留在首位）。
          const summaryMsg = nonSystemMessages[lastSummaryIdx]
          const afterSummary = nonSystemMessages.slice(lastSummaryIdx + 1)
          const boundedAfter = (maxCtx > 0 && afterSummary.length > maxCtx)
            ? afterSummary.slice(-maxCtx)
            : afterSummary
          console.info(`📃 上下文摘要窗口：剔除摘要前 ${lastSummaryIdx} 条旧消息，保留摘要 + 其后 ${boundedAfter.length} 条`)
          truncatedMessages = [...systemMessages, summaryMsg, ...boundedAfter]
        }
        else if (maxCtx > 0 && nonSystemMessages.length > maxCtx) {
          // 无摘要兜底：硬截最近 maxCtx 条
          console.info(`✂️ 上下文截断（无摘要兜底）：${nonSystemMessages.length} 条消息 → 保留最近 ${maxCtx} 条`)
          truncatedMessages = [...systemMessages, ...nonSystemMessages.slice(-maxCtx)]
        }

        const newMessages = truncatedMessages.map((msg) => {
          // ✨ 为用户消息添加发送者前缀
          if (msg.role === 'user') {
            const rawMsg = toRaw(msg)
            // 群聊需要 [名字]: 前缀区分多个成员；单聊只有两方，role 已能区分，
            // 加前缀反而是在给模型演示被系统提示词明令禁止的 [角色名]: 输出格式，
            // 是"掉格式"的上游根因。故单聊不加前缀（空串），群聊保留。
            const prefix = isGroupChat ? `[${userName}]: ` : ''

            if (typeof rawMsg.content === 'string') {
              return {
                ...rawMsg,
                content: `${prefix}${rawMsg.content}`,
              }
            }
            else if (Array.isArray(rawMsg.content)) {
            // 处理多模态内容
              // 检查是否包含表情包 (使用类型断言避免 TypeScript 检查)
              const firstPart = rawMsg.content[0] as any
              const hasStickerOnly = rawMsg.content.length === 1 && firstPart.type === 'sticker'

              if (hasStickerOnly) {
                // 表情包消息：优先发送图片给支持识图的模型，同时附带文本描述作为回退
                return {
                  ...rawMsg,
                  content: [
                    { type: 'text', text: `${prefix}[用户发送了表情包: "${firstPart.name}"]` },
                    { type: 'image_url', image_url: { url: firstPart.url } },
                  ],
                }
              }

              // 其他多模态内容（如图片+文本），过滤掉 sticker 类型（LLM 不支持）
              // 关键：image_url part 必须深拷贝，避免下游压缩函数就地修改时污染 store 里的原始引用
              const newContent = rawMsg.content.map((part) => {
                const p = part as any
                if (p.type === 'sticker') {
                  return { type: 'image_url', image_url: { url: p.url } }
                }
                if (p.type === 'image_url') {
                  return { type: 'image_url', image_url: { url: p.image_url?.url ?? '' } }
                }
                if ('text' in part) {
                  return { ...part, text: `${prefix}${part.text}` }
                }
                return part
              })
              return {
                ...rawMsg,
                content: newContent,
              }
            }
          }
          // ✨ 为AI消息添加发送者前缀
          else if (msg.role === 'assistant') {
            const msgAny = msg as any
            const { slices: _, ...rest } = msgAny // exclude slices
            if (rest.tool_results) {
              rest.tool_results = toRaw(rest.tool_results)
            }
            const rawMsg = toRaw(rest)
            // 同上：单聊不加 [名字]: 前缀，群聊保留（多成员需区分说话人）
            const prefix = isGroupChat ? `[${characterName}]: ` : ''

            // 模拟图片只把描述转换成文本上下文，避免把 UI 专用字段直接交给模型。
            if (Array.isArray(rawMsg.simulatedImages) && rawMsg.simulatedImages.length > 0) {
              const countLabel = rawMsg.simulatedImages.length > 1 ? `${rawMsg.simulatedImages.length}张` : '一张'
              const imageContext = rawMsg.simulatedImages.map((image: any, index: number) => {
                return `图片${index + 1}：${String(image.description).trim()}`
              }).join('\n')
              return {
                role: 'assistant',
                content: `${prefix}[发送了${countLabel}模拟图片]\n${imageContext}`,
              } as Message
            }

            if (typeof rawMsg.content === 'string') {
              return {
                ...rawMsg,
                content: `${prefix}${rawMsg.content}`,
              }
            }
            // AI消息通常是字符串，但为了安全也处理数组情况（过滤 sticker 类型）
            // 关键：image_url part 必须深拷贝，避免下游压缩函数就地修改时污染 store 里的原始引用
            else if (Array.isArray(rawMsg.content)) {
              const newContent = rawMsg.content.map((part: any) => {
                if (part.type === 'sticker') {
                  return { type: 'image_url', image_url: { url: part.url } }
                }
                if (part.type === 'image_url') {
                  return { type: 'image_url', image_url: { url: part.image_url?.url ?? '' } }
                }
                if ('text' in part) {
                  return { ...part, text: `${prefix}${part.text}` }
                }
                return part
              })
              return {
                ...rawMsg,
                content: newContent,
              }
            }
            return rawMsg
          }
          // ✨ 内联摘要消息：LLM API 不认识 'summary' role，转成 system 消息喂给模型
          else if ((msg as any).role === 'summary') {
            const rawMsg = toRaw(msg) as any
            return {
              role: 'system',
              content: `[以下是这段对话早期内容的摘要，请据此保持上下文连贯]\n${rawMsg.content}`,
            } as Message
          }
          return toRaw(msg)
        }) as Message[]

        // 如果是第一次互动且跳过了用户消息，添加一个系统提示让 AI 主动打招呼
        if (isFirstInteraction && options.skipUserMessage && !newMessages.some(message => message.role === 'user')) {
          newMessages.push({
            role: 'user',
            content: 'Please introduce yourself and greet the user naturally based on your character setting. Keep it friendly and in character.',
          } as Message)
        }

        // ⚡ 优化：图片压缩不阻塞发送，后台异步处理
        compressLargeDataUrlsInMessages(newMessages, {
          onImageDropped: (droppedUrl) => {
            console.info(`🧹 历史图片已静默跳过: ${safeHostname(droppedUrl)}`)
          },
        }).then(({ replacements: compressedUrls, dropped: droppedUrls }) => {
          if (compressedUrls.size === 0 && droppedUrls.length === 0)
            return

          import('./stickers').then(({ useStickersStore }) => {
            const stickersStore = useStickersStore()
            for (const [oldUrl, newUrl] of compressedUrls) {
              stickersStore.updateStickerUrlGlobally(oldUrl, newUrl)
            }

            const droppedSet = new Set(droppedUrls)
            targetMessagesRef.value = targetMessagesRef.value.map((msg) => {
              if (!Array.isArray(msg.content))
                return msg
              let changed = false
              const newContent = (msg.content as Array<{ type: string, url?: string, image_url?: { url: string } }>).flatMap((part) => {
                if (part.type === 'sticker' && part.url) {
                  if (compressedUrls.has(part.url)) {
                    changed = true
                    return [{ ...part, url: compressedUrls.get(part.url)! }]
                  }
                  if (droppedSet.has(part.url)) {
                    changed = true
                    return []
                  }
                }
                if (part.type === 'image_url' && part.image_url?.url) {
                  const currentUrl = part.image_url.url
                  if (compressedUrls.has(currentUrl)) {
                    changed = true
                    return [{ ...part, image_url: { ...part.image_url, url: compressedUrls.get(currentUrl)! } }]
                  }
                  if (droppedSet.has(currentUrl)) {
                    changed = true
                    return []
                  }
                }
                return [part]
              })
              return changed ? { ...msg, content: newContent } as typeof msg : msg
            })
            if (compressedUrls.size > 0)
              console.info(`🗜️ 懒压缩了 ${compressedUrls.size} 张图片并更新了 store`)
            if (droppedUrls.length > 0)
              console.info(`🧹 清理了 ${droppedUrls.length} 张无法加载的图片`)
          }).catch(() => { /* 压缩失败不影响聊天 */ })
        }).catch(() => { /* 压缩失败不影响聊天 */ })

        const modelCacheKey = getNonVisionModelCacheKey(options.providerId, options.model)
        nonVisionModelCache.value = pruneExpiredNonVisionModelCache(nonVisionModelCache.value)
        const payloadMessages = isNonVisionModelCached(nonVisionModelCache.value, modelCacheKey)
          ? sanitizeMessagesForNonVisionModel(newMessages as Message[])
          : newMessages as Message[]

        for (const hook of onAfterMessageComposedHooks.value) {
          await hook(sendingMessage)
        }

        for (const hook of onBeforeSendHooks.value) {
          await hook(sendingMessage)
        }

        let fullText = ''
        let nativeToolsDispatched = false
        const headers = (options.providerConfig?.headers || {}) as Record<string, string>

        // 创建一个 Promise，在流完成时才 resolve
        // 🔧 添加超时保护：防止 stream 在某些错误（如 401）下既不 resolve 也不 reject
        const runStreamRequest = (messagesForModel: Message[]) => new Promise<void>((resolve, reject) => {
          const streamAbortController = new AbortController() // 超时时中止底层流连接
          let settled = false
          let timeoutId: ReturnType<typeof setTimeout>
          let firstChunkReceived = false
          const safeResolve = () => {
            if (!settled) {
              settled = true
              clearTimeout(timeoutId)
              resolve()
            }
          }
          const safeReject = (err: unknown) => {
            if (!settled) {
              settled = true
              clearTimeout(timeoutId)
              streamAbortController.abort() // 中止底层流连接，防止资源泄漏
              reject(err)
            }
          }
          // 空闲计时器：首字到达前使用 AI_FIRST_RESPONSE_TIMEOUT_MS，首字后使用 AI_STREAM_IDLE_TIMEOUT_MS
          const resetIdleTimer = () => {
            clearTimeout(timeoutId)
            const ms = firstChunkReceived ? AI_STREAM_IDLE_TIMEOUT_MS : AI_FIRST_RESPONSE_TIMEOUT_MS
            timeoutId = setTimeout(() => {
              safeReject(new Error(
                firstChunkReceived
                  ? 'AI 对话超时（90秒内无新内容），请检查网络或 API 配置'
                  : 'AI 对话超时（等待响应超时），请检查网络或 API 配置',
              ))
            }, ms)
          }
          resetIdleTimer()

          // 工具调用气泡追踪：toolCallId → messages 数组下标，用于结果回填
          const toolCallMessageIndices = new Map<string, number>()
          const internalChatActionToolCallIds = new Set<string>()

          // native tool calling 已处理标记：任一终止工具执行后置 true，
          // 防止 finish 事件里 fullText='' 时误触发"AI 返回空响应"兜底通知。
          // 注意：声明在外层，重试分支会将其重置为 false。

          // 构建聊天动作工具上下文（native terminal tool calling）
          const chatActionPostProcessCtx: PostProcessContext = {
            messages: targetMessagesRef,
            targetContactId,
            pushMessageToTarget,
            getTargetMessages: () => messagesByContact.value[targetContactKey] ?? [],
            notifyMessagesChanged,
            saveMessages: messagesByContactStorage.save,
            memoryStore,
            contactsStore,
            airiCardStore,
            postsStore,
            relationshipStore,
            recallMessage,
          }
          // Store 字段按用户的通话方向命名；工具状态按 AI 收到/发起的方向命名。
          let callState: ChatToolContext['callState'] = 'idle'
          if (callStore.isCallActive)
            callState = 'active'
          else if (callStore.outgoingCallPending)
            callState = 'incoming'
          else if (callStore.incomingCallOffer)
            callState = 'outgoing'

          const hasCurrentFriendRequest = activeContact?.blockStatus?.status === 'pending_ai_approval'
            && newMessages.some((message: any) =>
              message.role === 'system'
              && message.metadata?.type === 'friend_request_application_prompt',
            )
          const taskKind = options.taskKind ?? (hasCurrentFriendRequest ? 'friend-request-decision' : undefined)

          const nativeChatToolContext: ChatToolContext = {
            dispatchMessages: async (items, innerVoice) => {
              const ctx: MessageDispatchContext = {
                messages: targetMessagesRef,
                streamingMessage,
                notifyMessagesChanged,
                contactsStore,
                callStore,
                pluginsStore,
                isGroupChat,
                characterName: activeContact?.name ?? airiCardStore.characterName ?? 'AI',
                options: { skipUserMessage: options.skipUserMessage },
                onTokenSpecialHooks,
                setAiTypingState,
                broadcastMessage,
                sendDesktopNotification,
                targetContactId,
                pushMessageToTarget,
                getTargetMessages: () => messagesByContact.value[targetContactKey] ?? [],
              }
              await dispatchMultipleMessages(items, ctx, innerVoice)
              nativeToolsDispatched = true
            },
            postProcessCtx: chatActionPostProcessCtx,
            targetContactId,
            callState,
            isGroupChat,
            taskKind,
            onTerminalToolHandled: () => {
              nativeToolsDispatched = true
            },
            onActionToolHandled: () => {
              nativeToolsDispatched = true
            },
            onToolBudgetExceeded: () => {
              nativeToolsDispatched = true
              insertSystemNotification('本轮操作过多，已停止继续执行', 'i-solar:danger-triangle-bold', {
                messages: targetMessagesRef,
                notifyMessagesChanged,
                pushMessageToTarget,
              })
            },
            allowedOfflineModeSwitch: isGroupChat || callStore.isCallActive || callStore.outgoingCallPending
              ? undefined
              : isOfflineMode.value
                ? 'online'
                : activeContact?.offlineModeEnabled
                  ? 'offline'
                  : undefined,
            onCallInitiate: (type, greeting) => {
              // native 路径下 fullText 为空、parsed.callIntent 恒 undefined，finish handler 的
              // handleAIInitiatedCall 接不到意图，故这里直接驱动 callStore（逻辑对齐 handleAIInitiatedCall）。
              if (callStore.isCallActive || callStore.outgoingCallPending || callStore.incomingCallOffer) {
                console.warn('[chat-action] 已有通话进行中，忽略 initiate_call')
                return
              }
              // 工具调用可能跨越联系人切换；只允许作用于本轮开始时锁定的联系人。
              const conversationContact = targetContactId ? contactsStore.getContact(targetContactId) : null
              const characterId = conversationContact?.id
                || String(airiCardStore.activeCard?.metadata?.id || airiCardStore.activeCard?.name || 'AI')
              const characterName = conversationContact?.name || airiCardStore.activeCard?.name || 'AI'
              const characterAvatar = conversationContact?.avatar || '/BOT.jpg'
              callStore.setIncomingCall(type, characterId, characterName, characterAvatar, greeting)
              console.info(`📞 [chat-action] AI 通过 native 工具发起${type}通话`)
            },
            onHangUp: (reason) => {
              // 同理：native 路径 parsed.hangUpIntent 恒 undefined，直接调 handleAIHangUp。
              if (!callStore.isCallActive) {
                console.warn('[chat-action] 当前无通话，忽略 hang_up_call')
                return
              }
              console.info('📞 [chat-action] AI 通过 native 工具挂断通话')
              void handleAIHangUp(reason)
            },
            onFriendRequestResponse: (decision, message) => {
              const aiContactId = targetContactId
              if (!aiContactId)
                return
              const aiContact = contactsStore.getContact(aiContactId)
              if (!aiContact || aiContact.blockStatus?.status !== 'pending_ai_approval')
                return
              if (decision === 'accept') {
                contactsStore.acceptUserRefriendRequest(aiContactId)
                purgeApplicationPromptMessages(aiContactId)
              }
              else {
                contactsStore.rejectUserRefriendRequest(aiContactId)
                purgeApplicationPromptMessages(aiContactId)
              }
              if (message) {
                pushMessageToTarget({
                  role: 'assistant',
                  content: message,
                  slices: [{ type: 'text', text: message }],
                  tool_results: [],
                  timestamp: Date.now(),
                } as any)
              }
            },
            onBlockUser: (reason) => {
              const aiContactId = targetContactId
              if (!aiContactId)
                return
              const aiContact = contactsStore.getContact(aiContactId)
              if (!aiContact || aiContact.isGroup)
                return
              const currentStatus = aiContact.blockStatus?.status
              if (currentStatus === 'blocked_by_ai' || currentStatus === 'pending_ai_approval')
                return
              if (aiContact.aiBlockUserEnabled === false)
                return
              contactsStore.setBlockedByAi(aiContactId)
              pushMessageToTarget({
                role: 'system',
                content: `${aiContact.name} 拉黑了你`,
                timestamp: Date.now(),
                metadata: { type: 'block_by_ai', reason },
              } as any)
            },
            onSpeakInCall: (dialogue) => {
              // native 工具路径：dialogue 在工具参数里，fullText 为空，
              // 故 finish handler 的 handleCallTranscript 不会写转录，此处直接委托纯函数。
              handleSpeakInCall(dialogue, {
                callStore,
                contactsStore,
                airiCardStore,
                bgActivityStore,
                messages: targetMessagesRef,
                notifyMessagesChanged,
                triggerFullTextSpeech,
                send,
                sendOptions: { providerId: options.providerId, model: options.model, chatProvider: options.chatProvider as any, providerConfig: options.providerConfig },
              })
            },
            onAnswerCall: (decision, message) => {
              // native 工具路径：替代 handleCallDecision 的 JSON 解析，直接委托纯函数。
              handleAnswerCall(decision, message, {
                callStore,
                contactsStore,
                airiCardStore,
                bgActivityStore,
                messages: targetMessagesRef,
                notifyMessagesChanged,
                triggerFullTextSpeech,
                send,
                sendOptions: { providerId: options.providerId, model: options.model, chatProvider: options.chatProvider as any, providerConfig: options.providerConfig },
              })
            },
          }

          // 🆕 群聊工具上下文扩展（在 nativeChatToolContext 基础上添加群聊回调）
          let finalChatToolContext = nativeChatToolContext
          if (isGroupChat) {
            finalChatToolContext = {
              ...nativeChatToolContext,
              // group_chat_response 工具的落地：复用旧 JSON 路径的 parseMessagesArray + 假打字机分发
              dispatchGroupMessages: async (groupMessages) => {
                try {
                  nativeToolsDispatched = true

                  // pat 不是可渲染消息（mapRawItem 无 pat 分支，会被当空消息丢掉），
                  // 单独摘出来走 usePatInteraction，与旧 JSON 路径的 parsed.patAction 语义对齐。
                  const patItems = groupMessages.filter(m => m.type === 'pat')
                  const speakItems = groupMessages.filter(m => m.type !== 'pat')

                  // parseMessagesArray(parsed, fullText)：构造最小 ParsedAIResponse 走群聊分支
                  const items = parseMessagesArray({ text: '', messages: speakItems }, '')

                  const ctx: MessageDispatchContext = {
                    messages: targetMessagesRef,
                    streamingMessage,
                    notifyMessagesChanged,
                    contactsStore,
                    callStore,
                    pluginsStore,
                    isGroupChat: true,
                    characterName: activeContact?.name ?? 'Group',
                    options: { skipUserMessage: options.skipUserMessage },
                    onTokenSpecialHooks,
                    setAiTypingState,
                    broadcastMessage,
                    sendDesktopNotification,
                    targetContactId,
                    pushMessageToTarget,
                    getTargetMessages: () => messagesByContact.value[targetContactKey] ?? [],
                  }

                  if (items.length > 0)
                    await dispatchMultipleMessages(items, ctx)

                  // 正文先分发完再拍一拍，避免动作抢在回复前出现（与 finish handler 的 patAction 顺序一致）
                  if (patItems.length > 0 && targetContactId) {
                    const { usePatInteraction } = await import('../composables/usePatInteraction')
                    // 一轮只允许一次 pat，多个只取第一个，防止动作刷屏
                    const actor = contactsStore.getContact(patItems[0].senderId)
                    if (actor) {
                      usePatInteraction().aiPatUser(targetContactId, actor.id, actor.name)
                      console.info(`👋 [group_chat_response] ${actor.name} 拍了拍用户`)
                    }
                    else {
                      console.warn(`⚠️ [group_chat_response] pat 的 senderId 无效，已跳过: ${patItems[0].senderId}`)
                    }
                  }
                }
                catch (error) {
                  console.error('[group_chat_response] 群聊消息分发失败:', error)
                }
              },
              // 传递群成员列表给工具构建（senderId 的 enum 白名单）
              groupMembers: (activeContact?.members || [])
                .map((id: string) => {
                  const member = contactsStore.getContact(id)
                  return member ? { id: member.id, name: member.name } : null
                })
                .filter((m): m is { id: string, name: string } => m !== null),
            }
          }

          stream(options.model, options.chatProvider, messagesForModel, {
            headers,
            abortSignal: streamAbortController.signal, // 传递 abort signal 给底层流
            chatToolContext: finalChatToolContext,
            async onStreamEvent(event: StreamEvent) {
              try {
                // 内部聊天动作（send_messages/silent/记忆/关系等）不显示「正在使用工具」气泡，
                // 与旧 JSON 格式行为一致；只有真正的 MCP/调试工具才显示。
                const eventToolName = (event as any).toolName as string | undefined
                const isChatActionTool = eventToolName ? CHAT_ACTION_TOOL_NAMES.has(eventToolName) : false

                if (event.type === 'tool-call-streaming-start') {
                  if (isChatActionTool) {
                    internalChatActionToolCallIds.add(event.toolCallId)
                    resetIdleTimer()
                    return
                  }
                  // 插入工具调用指示气泡（复用 plugin_notification 样式，可展开）
                  resetIdleTimer()
                  const displayName = getToolDisplayName((event as any).toolName)
                  const toolMsg: TimestampedSystemMessage = {
                    role: 'system',
                    content: `正在${displayName}`,
                    timestamp: Date.now(),
                    metadata: {
                      type: 'plugin_notification',
                      toolCallId: event.toolCallId,
                      status: 'calling',
                    },
                  }
                  targetMessagesRef.value.push(toolMsg as any)
                  toolCallMessageIndices.set(event.toolCallId, targetMessagesRef.value.length - 1)
                }
                else if (event.type === 'tool-call') {
                  resetIdleTimer()
                  if (CHAT_ACTION_TOOL_NAMES.has(event.toolName)) {
                    internalChatActionToolCallIds.add(event.toolCallId)
                    return
                  }
                  toolCallQueue.enqueue({
                    type: 'tool-call',
                    toolCall: event,
                  })
                  // 回填参数到对应气泡
                  const idx = toolCallMessageIndices.get(event.toolCallId)
                  if (idx !== undefined) {
                    const msg = targetMessagesRef.value[idx] as any
                    if (msg?.metadata) {
                      msg.metadata.content = `${getToolDisplayName(event.toolName)}已完成`
                      msg.metadata.args = JSON.stringify((event as any).args ?? {}, null, 2)
                    }
                  }
                }
                else if (event.type === 'tool-result') {
                  resetIdleTimer()
                  if (internalChatActionToolCallIds.delete(event.toolCallId))
                    return
                  toolCallQueue.enqueue({
                    type: 'tool-call-result',
                    id: event.toolCallId,
                    result: event.result,
                  })
                  // 回填结果到对应气泡，组合成可展开的 details
                  const idx = toolCallMessageIndices.get(event.toolCallId)
                  if (idx !== undefined) {
                    const msg = targetMessagesRef.value[idx] as any
                    if (msg?.metadata) {
                      msg.metadata.status = 'done'
                      const resultText = typeof event.result === 'string'
                        ? event.result
                        : JSON.stringify(event.result, null, 2)
                      const argsText = msg.metadata.args ?? ''
                      msg.metadata.details = argsText
                        ? `参数：\n${argsText}\n\n结果：\n${resultText}`
                        : `结果：\n${resultText}`
                    }
                  }
                }
                else if (event.type === 'reasoning-delta') {
                  // 厂商原生思考链不展示也不入库（那是模型的元推理，不是角色的内心）；
                  // 仍需喂 idle 计时器，否则长思考期间没有正文 delta 会被误判为卡死。
                  resetIdleTimer()
                }
                else if (event.type === 'text-delta') {
                  firstChunkReceived = true
                  resetIdleTimer()
                  fullText += event.text
                  await parser.consume(event.text)
                }
                else if (event.type === 'finish') {
                  // 🔧 如果已经因 error 事件 reject 了 Promise，跳过 finish 处理
                  // （自定义 fetch 检测到 HTTP 错误后返回空响应，streamText 会触发一个空的 finish）
                  if (settled) {
                    console.info('ℹ️ Promise 已因错误 settled，跳过 finish 处理')
                    return
                  }

                  // 🔧 多步流式处理：maxSteps>1 时每步都会触发 finish
                  // 只在最终完成时处理（跳过工具调用的中间步骤）
                  const finishReason = (event as any).finishReason
                  if (finishReason !== 'stop') {
                    console.info(`🏁 finish事件触发，finishReason: ${finishReason}, 通话状态: ${callStore.isCallActive}, transcript长度: ${callStore.currentTranscript.length}`)
                  }

                  if (finishReason === 'tool-calls') {
                    console.info('🔧 工具调用步骤完成，等待下一步...')
                    resetIdleTimer() // 工具执行可能耗时，重置空闲计时器
                    // 重置跨步累积状态，避免多步工具调用之间串联正文
                    fullText = ''
                    finishProcessed = false
                    parser = createParser()
                    return
                  }

                  // 防重复处理：如果已经处理过最终 finish，直接返回
                  // （maxSteps>1 时 streamText 库可能在最终步骤和流结束时各触发一次 finish，属于预期行为）
                  if (finishProcessed) {
                    console.info('ℹ️ finish事件重复触发（maxSteps多步流的预期行为），跳过处理')
                    return
                  }
                  finishProcessed = true

                  try {
                  // Finalize the parsing of the actual message content
                    await parser.end()

                    // 处理 AI 响应
                    const parsed = processAIResponse(fullText)

                    // 防抽风：线上模式下，模型偶尔会沿用线下格式输出 [动作描写]。
                    // 此处在后处理之前清洗掉方括号及其内容（仅针对线上一对一文字聊天）。
                    // 注意时机：switch_offline_mode 尚未在此处执行，isOfflineMode 仍反映本次回复
                    // 生成时的模式，因此线下告别消息不会被误删。
                    if (!isOfflineMode.value && !callStore.isCallActive && !callStore.outgoingCallPending && !activeContact?.isGroup) {
                      stripBracketDescriptionsFromResponse(parsed)
                    }

                    // ✨ 统一执行所有后处理（记忆/提醒/朋友圈/好感度/表情包）
                    const postProcessCtx: PostProcessContext = {
                      messages: targetMessagesRef,
                      targetContactId,
                      pushMessageToTarget,
                      getTargetMessages: () => messagesByContact.value[targetContactKey] ?? [],
                      notifyMessagesChanged,
                      saveMessages: messagesByContactStorage.save,
                      memoryStore,
                      contactsStore,
                      airiCardStore,
                      postsStore,
                      relationshipStore,
                      recallMessage,
                    }
                    await runAllPostProcessors(parsed, postProcessCtx)

                    // ⭐ 处理通话相关的AI决策（使用提取的模块）
                    const callCtx: CallHandlerContext = {
                      callStore,
                      contactsStore,
                      airiCardStore,
                      bgActivityStore,
                      messages: targetMessagesRef,
                      notifyMessagesChanged,
                      triggerFullTextSpeech,
                      send,
                      sendOptions: { providerId: options.providerId, model: options.model, chatProvider: options.chatProvider as any, providerConfig: options.providerConfig },
                    }

                    // 4️⃣ 检测通话决策（仅在等待接听时）
                    const callDecisionResult = handleCallDecision(fullText, handleAICallDecision, callCtx)
                    if (callDecisionResult.shouldReturn) {
                      safeResolve()
                      return
                    }

                    // 5️⃣ 处理AI主动发起通话（包括后台活动触发的）
                    const aiCallResult = handleAIInitiatedCall(fullText, detectCallIntent, callCtx, parsed.callIntent)
                    if (aiCallResult.shouldReturn) {
                      safeResolve()
                      return
                    }

                    // 6️⃣ 处理AI在通话中的挂断
                    const hangUpResult = await handleCallHangUp(fullText, detectHangUpIntent, handleAIHangUp, callCtx, parsed.hangUpIntent)
                    if (hangUpResult.shouldReturn) {
                      safeResolve()
                      return
                    }

                    // 6️⃣.5 处理 AI 拉黑用户 / 回应好友申请
                    // 状态切换（setBlockedByAi 等）立即生效以保证后续守卫，
                    // 但相关系统消息延后到 dispatchMultipleMessages 完成后再 push，
                    // 避免出现「系统消息抢先弹出 → AI 告别消息打字慢悠悠」的时序错乱。
                    const deferredSystemMessages: Array<{ role: 'system', content: string, timestamp: number, metadata: Record<string, any> }> = []
                    const deferredAssistantReplies: Array<any> = []
                    const aiContactId = targetContactId
                    if (aiContactId) {
                      const aiContact = contactsStore.getContact(aiContactId)
                      if (aiContact && !aiContact.isGroup) {
                        // AI 拉黑用户
                        if (parsed.blockUserIntent) {
                          // 守卫 0：联系人是否允许 AI 主动拉黑（默认 true）
                          if (aiContact.aiBlockUserEnabled === false) {
                            console.warn(`⚠️ [chat] AI 想拉黑用户，但联系人「${aiContact.name}」已关闭"AI 主动拉黑"开关，忽略 block_user`)
                          }
                          // 守卫 1：已经处于 blocked_by_ai 或 pending_ai_approval 时不要重复拉黑
                          // 防止 AI 因为 prompt cache 复读旧 action
                          else {
                            const currentStatus = aiContact.blockStatus?.status
                            if (currentStatus === 'blocked_by_ai') {
                              console.warn(`⚠️ [chat] AI 想拉黑用户，但用户已被 AI 拉黑（状态: ${currentStatus}），忽略 block_user`)
                            }
                            else if (currentStatus === 'pending_ai_approval') {
                              console.warn(`⚠️ [chat] AI 想拉黑用户，但当前是好友申请决策状态（${currentStatus}），忽略 block_user。AI 应当使用 friend_request_response 工具`)
                            }
                            else {
                              contactsStore.setBlockedByAi(aiContactId)
                              deferredSystemMessages.push({
                                role: 'system',
                                content: `${aiContact.name} 拉黑了你`,
                                timestamp: Date.now(),
                                metadata: { type: 'block_by_ai', reason: parsed.blockUserIntent.reason },
                              })
                              console.info(`🚫 AI 拉黑了用户: ${aiContact.name}`)
                            }
                          }
                        }
                        // AI 回应用户好友申请
                        if (parsed.friendRequestResponse) {
                          console.info('✋ [chat] 检测到 friendRequestResponse', {
                            decision: parsed.friendRequestResponse.decision,
                            message: parsed.friendRequestResponse.message,
                            currentBlockStatus: aiContact.blockStatus?.status,
                          })
                          if (aiContact.blockStatus?.status !== 'pending_ai_approval') {
                            console.warn(`⚠️ [chat] AI 想回应好友申请，但当前状态不是 pending_ai_approval（实际: ${aiContact.blockStatus?.status ?? '无'}），忽略`)
                          }
                          else if (parsed.friendRequestResponse.decision === 'accept') {
                            contactsStore.acceptUserRefriendRequest(aiContactId)
                            // F4：状态推进后清理给 AI 看的 application_prompt
                            purgeApplicationPromptMessages(aiContactId)
                            const messageText = parsed.friendRequestResponse.message?.trim()
                            if (messageText) {
                              // 把 message 作为 AI 的回复消息插入聊天记录（用户看得到）
                              deferredAssistantReplies.push({
                                role: 'assistant',
                                content: messageText,
                                timestamp: Date.now(),
                                slices: [{ type: 'text', text: messageText }],
                                tool_results: [],
                                metadata: { type: 'friend_request_accept_reply' },
                              })
                            }
                            deferredSystemMessages.push({
                              role: 'system',
                              content: `${aiContact.name || '对方'} 通过了你的好友申请`,
                              timestamp: Date.now() + 1,
                              metadata: { type: 'friend_request_accepted' },
                            })
                            console.info(`✅ [chat] AI 接受了好友申请: ${aiContact.name}`)
                          }
                          else {
                            contactsStore.rejectUserRefriendRequest(aiContactId)
                            // F4：状态推进后清理给 AI 看的 application_prompt
                            purgeApplicationPromptMessages(aiContactId)
                            const messageText = parsed.friendRequestResponse.message?.trim()
                            if (messageText) {
                              deferredAssistantReplies.push({
                                role: 'assistant',
                                content: messageText,
                                timestamp: Date.now(),
                                slices: [{ type: 'text', text: messageText }],
                                tool_results: [],
                                metadata: { type: 'friend_request_reject_reply' },
                              })
                            }
                            deferredSystemMessages.push({
                              role: 'system',
                              content: `${aiContact.name || '对方'} 拒绝了你的好友申请`,
                              timestamp: Date.now() + 1,
                              metadata: { type: 'friend_request_rejected' },
                            })
                            console.info(`❌ [chat] AI 拒绝了好友申请: ${aiContact.name}`)
                          }
                        }

                        else if (aiContact.blockStatus?.status === 'pending_ai_approval') {
                          // F1：AI 拿到 friend_request_application_prompt 但没调用 friend_request_response 工具。
                          // H4 (review)：仅当 AI 真正"零内容沉默"时视为隐式拒绝并推进到 blocked_by_ai；
                          // 如果 AI 返回了普通文本（"嗯…我想想"等），保持 pending 让用户/下一轮 send 推进，
                          // 避免出现"AI 边说话边把我拒了"的体感割裂。
                          // 真正的死锁源（无限烧 token）已经被 F2 删除后台兜底切断，pending 长时间停留不会再循环烧 token。
                          const hasTextOutput = (fullText && fullText.trim().length > 0)
                            || streamingMessage.value.slices.length > 0
                          if (hasTextOutput) {
                            console.warn(`⚠️ [chat] 联系人「${aiContact.name}」处于 pending_ai_approval，AI 返回了文本但未调用 friend_request_response，保持 pending 由后续交互推进`)
                          }
                          else {
                            console.warn(`⚠️ [chat] 联系人「${aiContact.name}」处于 pending_ai_approval 且本轮零内容沉默，视为隐式拒绝`)
                            contactsStore.rejectUserRefriendRequest(aiContactId)
                            // F4：清理 application_prompt 避免下一轮请求继续诱导 AI 沉默
                            purgeApplicationPromptMessages(aiContactId)
                            deferredSystemMessages.push({
                              role: 'system',
                              content: `${aiContact.name || '对方'} 拒绝了你的好友申请`,
                              timestamp: Date.now() + 1,
                              metadata: { type: 'friend_request_rejected', reason: 'implicit_silent_reject' },
                            })
                          }
                        }
                      }
                    }

                    // 7️⃣ 处理AI在通话中的正常回复（添加到转录）
                    handleCallTranscript(parsed.text, callCtx)

                    // 兼容旧版结构化响应：若解析出纯文本，用它替换流式原文
                    if (parsed.text !== fullText) {
                      console.debug('🔄 检测到旧版结构化响应，提取纯文本内容')

                      // 清空之前的 slices（可能包含旧格式结构）
                      streamingMessage.value.slices = []
                      streamingMessage.value.content = parsed.text

                      // 添加解析后的纯文本作为新的slice
                      if (parsed.text) {
                        streamingMessage.value.slices.push({
                          type: 'text',
                          text: parsed.text,
                        })
                      }
                    }
                    else if (fullText.trim() && parsed.text === fullText) {
                      console.info('✅ 响应是纯文本格式')
                    }

                    // 🆕 解析消息数组（群聊多角色 / 普通多条消息）
                    const messagesArray = parseMessagesArray(parsed, fullText)

                    // 🆕 构建消息分发上下文
                    const dispatchCtx: MessageDispatchContext = {
                      messages: targetMessagesRef,
                      streamingMessage,
                      notifyMessagesChanged,
                      contactsStore,
                      callStore,
                      pluginsStore,
                      isGroupChat,
                      characterName,
                      options: { skipUserMessage: options.skipUserMessage },
                      onTokenSpecialHooks,
                      setAiTypingState,
                      broadcastMessage,
                      sendDesktopNotification,
                      // 🔧 固定联系人消息操作，防止切换联系人后消息推到错误的列表
                      targetContactId,
                      pushMessageToTarget,
                      getTargetMessages: () => {
                        return messagesByContact.value[targetContactKey] ?? []
                      },
                    }

                    // 🆕 消息分发：多条 / 单条 / 兜底
                    if (parsed.isSilent) {
                      // AI 选择沉默（如对撤回不回应），跳过消息入库
                      console.info('🤫 AI 选择沉默，跳过消息分发')
                      setAiTypingState(false)
                      streamingMessage.value = { role: 'assistant', content: '', slices: [], tool_results: [] }
                    }
                    else if (messagesArray.length > 0) {
                      await dispatchMultipleMessages(messagesArray, dispatchCtx, parsed.innerVoice)
                    }
                    else if (!parsed.parseSuccess && (!parsed.text.trim() || parsed.text === LEAK_PLACEHOLDER) && fullText.trim()) {
                      // 旧版结构化回复解析失败时走系统通知兜底，避免把内部数据直接展示给用户。
                      dispatchFallbackMessage(dispatchCtx, fullText)
                    }
                    else if (streamingMessage.value.slices.length > 0 || (messagesArray.length === 0 && parsed.text && parsed.text.trim())) {
                      await dispatchSingleMessage(parsed, dispatchCtx)
                    }
                    else if (parsed.parseSuccess && !parsed.text.trim() && fullText) {
                      // 仅执行非发消息动作时，合法地静默结束本轮。
                      if (import.meta.env.DEV)
                        console.info('ℹ️ 无消息分发（AI执行了非发消息 action），静默处理')
                      setAiTypingState(false)
                      streamingMessage.value = { role: 'assistant', content: '', slices: [], tool_results: [] }
                    }
                    else {
                      // fullText 为空：API 返回空响应；fullText 有值：解析后无可渲染内容
                      // nativeToolsDispatched=true 表示终止工具已处理，finish 里 fullText='' 属正常，不报错
                      if (!nativeToolsDispatched)
                        dispatchFallbackMessage(dispatchCtx, fullText || undefined)
                    }

                    // AI 的正文先完成分发，再显示拍一拍系统提示，避免动作抢在回复前出现
                    if (parsed.patAction && targetContactId) {
                      const conversationContact = contactsStore.getContact(targetContactId)
                      const actorContact = conversationContact?.isGroup
                        ? (parsed.patAction.senderId ? contactsStore.getContact(parsed.patAction.senderId) : undefined)
                        : conversationContact

                      if (!actorContact) {
                        console.warn(
                          conversationContact?.isGroup
                            ? `⚠️ [chat] 群聊 pat action 缺少有效 senderId，已跳过: ${parsed.patAction.senderId ?? '(无)'}`
                            : '⚠️ [chat] pat action 找不到会话联系人，已跳过',
                        )
                      }
                      else {
                        const { usePatInteraction } = await import('../composables/usePatInteraction')
                        usePatInteraction().aiPatUser(
                          targetContactId,
                          actorContact.id,
                          actorContact.name,
                        )
                        console.info(`👋 [chat] AI 拍了拍用户: ${actorContact.name}`)
                      }
                    }

                    // 语音消息后处理：必须在 dispatch 之后执行，因为需要查找已分发的 messageType === 'voice_message' 消息
                    await handleVoiceMessageSend(parsed, postProcessCtx)

                    // 🆕 拉黑系统：dispatch 完成后再 flush 延后的系统/assistant 消息
                    // 确保「AI 告别消息」全部先打字完成，再弹出「X 拉黑了你」系统提示
                    if (aiContactId && (deferredAssistantReplies.length > 0 || deferredSystemMessages.length > 0)) {
                      for (const msg of deferredAssistantReplies)
                        pushMessageToContact(aiContactId, msg as any)
                      for (const msg of deferredSystemMessages)
                        pushMessageToContact(aiContactId, msg as any)
                    }

                    // Reset the streaming message for the next turn
                    streamingMessage.value = { role: 'assistant', content: '', slices: [], tool_results: [] }

                    // Instruct the TTS pipeline to flush by calling hooks directly
                    const flushSignal = `${TTS_FLUSH_INSTRUCTION}${TTS_FLUSH_INSTRUCTION}`
                    try {
                      for (const hook of onTokenLiteralHooks.value)
                        await hook(flushSignal)
                    }
                    catch (flushError) {
                      console.warn('TTS flush失败，继续:', flushError)
                    }

                    // Call the end-of-stream hooks
                    try {
                      for (const hook of onStreamEndHooks.value)
                        await hook()
                    }
                    catch (streamEndError) {
                      console.warn('Stream end hook失败，继续:', streamEndError)
                    }

                    // Call the end-of-response hooks with the full text
                    try {
                      for (const hook of onAssistantResponseEndHooks.value)
                        await hook(fullText)
                    }
                    catch (responseEndError) {
                      console.warn('Response end hook失败，继续:', responseEndError)
                    }

                    if (consumedPatContextMessages.size > 0) {
                      const targetMessages = messagesByContact.value[targetContactKey] ?? []
                      const remainingMessages = removeConsumedPatContextMessages(
                        targetMessages,
                        consumedPatContextMessages,
                      )
                      if (remainingMessages.length !== targetMessages.length) {
                        setMessagesForContact(targetContactKey, remainingMessages)
                      }
                    }

                    console.debug('LLM output:', fullText)

                    // 只有 AI 成功完成回复后才计为一轮，避免失败请求污染轮数。
                    incrementConversationTurns(targetContactId)
                    if (!options.skipUserMessage || options.countAsUserTurn)
                      memoryStore.incrementMessageCount(targetContactId)
                    setLastMessageTimeForContact(targetContactId, Date.now())

                    // 检查是否需要生成对话摘要（后台异步执行，不阻塞UI）
                    // Issue 2 修复：先判断本轮内联摘要是否会触发；若会，跳过 memory 摘要，
                    // 避免同一 finish 事件双发 AI 请求造成双倍 token 消耗。
                    const compressThreshold = settingsStore.maxContextMessages
                    const inlineSummaryWillFire = compressThreshold > 0
                      && !inlineSummaryGenerating.has(targetContactKey)
                      && (() => {
                        const listCheck = messagesByContact.value[targetContactKey] ?? []
                        const lastSumIdxCheck = listCheck.findLastIndex(m => (m as any).role === 'summary')
                        const toCompressCheck = listCheck
                          .slice(lastSumIdxCheck + 1)
                          .filter(m => m.role === 'user' || m.role === 'assistant')
                        return toCompressCheck.length >= compressThreshold
                      })()

                    if (memoryStore.shouldGenerateSummaryForContact(targetContactId) && !inlineSummaryWillFire) {
                      // 🔧 修复：只取「上次摘要 endTime 之后的新消息」，避免重复摘要已摘要过的内容。
                      // 同时排除 role:'summary' 的内联摘要消息，防止摘要再被摘要（语义循环）。
                      // 用 Math.max 取所有摘要的最大 endTime，不依赖数组排序方向。
                      const targetSummaries = memoryStore.summaries.filter(s => s.contactId === targetContactId)
                      const lastSummaryEndTime = targetSummaries.length > 0
                        ? Math.max(...targetSummaries.map(s => s.endTime))
                        : 0
                      const targetMessagesForDigest = messagesByContact.value[targetContactKey] ?? []
                      const newMessages = targetMessagesForDigest
                        .filter((m) => {
                          if (m.role === 'system')
                            return false
                          if ((m as any).role === 'summary')
                            return false
                          const ts = (m as any).timestamp as number | undefined
                          return ts == null || ts > lastSummaryEndTime
                        }) as Message[]

                      // 消息不足时跳过（避免对空/极少内容生成无意义摘要）
                      // 注意：不重置计数器，下次继续累积，避免永远触发不了摘要的死锁
                      if (newMessages.length < 2) {
                        console.info('⏭️ 新消息不足，跳过摘要生成')
                      }
                      else {
                        // 先持久化任务，再在前台异步处理；页面被 PWA 挂起/杀死后可在下次恢复。
                        // 整理请求与摘要共用一次 LLM 调用，同时提炼情感记忆、承诺和未完成话题。
                        const digestJob = await memoryStore.enqueueDigestJob(newMessages, targetContactId)
                        if (digestJob) {
                          // finish 事件仍处于本次发送流程内，等待发送状态释放后再尝试，
                          // 防止整理请求与下一次正常聊天请求并发。
                          void (async () => {
                            for (let attempt = 0; attempt < 40; attempt++) {
                              if (!sending.value) {
                                const processed = await memoryStore.processPendingDigestJobs(
                                  options.model,
                                  options.chatProvider,
                                  () => !sending.value,
                                )
                                if (processed) {
                                  console.info('✅ 对话摘要与记忆整理成功')
                                  return
                                }
                              }
                              await new Promise(resolve => setTimeout(resolve, 250))
                            }
                            console.info('⏭️ 记忆整理任务已保存，等待下次前台重试')
                          })().catch((summaryError: unknown) => {
                            console.warn('⚠️ 记忆整理任务调度失败，将在下次前台恢复后重试:', summaryError)
                          })
                        }
                      }
                    }

                    // 🔄 Operit 式滚动摘要：自上条内联摘要以来的对话消息累积到阈值，
                    // 压缩成一条 role:'summary' 消息插入历史，后续请求只发「摘要 + 其后消息」。
                    // 与上方 memoryStore 摘要系统并存互补（那是 system prompt 侧记忆检索）。
                    // compressThreshold 和 inlineSummaryWillFire 已在上方计算，直接复用
                    if (inlineSummaryWillFire) {
                      const listNow = messagesByContact.value[targetContactKey] ?? []
                      const lastSummaryIdx = listNow.findLastIndex(m => (m as any).role === 'summary')
                      const toCompress = listNow
                        .slice(lastSummaryIdx + 1)
                        .filter(m => m.role === 'user' || m.role === 'assistant') as Message[]

                      if (toCompress.length >= compressThreshold) {
                        // C1 修复：标记正在生成，防止并发重复触发
                        inlineSummaryGenerating.add(targetContactKey)
                        // 边界：最后一条被压缩消息的 timestamp，摘要插到它之后
                        const boundaryTs = (toCompress.at(-1) as any)?.timestamp as number | undefined
                        // Issue 3 修复：用 AbortController 在超时时中止 stream，避免游离请求
                        const abortController = new AbortController()
                        const timeoutId = setTimeout(() => abortController.abort(), 30000)
                        // fire-and-forget，不阻塞 finish
                        generateInlineSummary(options.model, options.chatProvider, toCompress, abortController.signal)
                          .then((summaryMsg) => {
                            clearTimeout(timeoutId)
                            if (!summaryMsg)
                              return
                            const list = messagesByContact.value[targetContactKey]
                            if (!list)
                              return
                              // C2/H5 修复：找不到 boundary 时放弃插入，不静默降级到末尾
                            if (boundaryTs == null) {
                              console.warn('⚠️ 内联摘要：无法确定插入位置（无 boundaryTs），放弃本次插入')
                              return
                            }
                            const foundIdx = list.findIndex(m => (m as any).timestamp === boundaryTs)
                            if (foundIdx === -1) {
                              console.warn('⚠️ 内联摘要：boundary 消息已不存在（可能被删除），放弃插入')
                              return
                            }
                            list.splice(foundIdx + 1, 0, summaryMsg as any)
                            notifyMessagesChanged()
                            console.info(`🔄 内联摘要已插入：压缩 ${summaryMsg.coveredCount} 条对话`)
                          })
                          .catch((e) => {
                            clearTimeout(timeoutId)
                            console.warn('⚠️ 内联摘要生成失败，下轮重试:', e)
                          })
                          .finally(() => {
                            // C1 修复：无论成败都清除标志，允许下一轮触发
                            inlineSummaryGenerating.delete(targetContactKey)
                          })
                      }
                    }
                  }
                  catch (finishError) {
                    console.error('处理finish事件时出错:', finishError)
                  }
                  finally {
                  // 🔑 关键：无论如何都要 resolve Promise，确保 sending 状态被重置
                    console.debug('✅ 流处理完成，重置sending状态')
                    setAiTypingState(false)
                    safeResolve()
                  }
                }
                else if (event.type === 'error') {
                // 标记调用失败
                  lastCallFailed.value = true
                  safeReject(event.error)
                }
              }
              catch (error) {
                safeReject(error)
              }
            },
          }).catch(safeReject) // 如果 stream 函数本身出错，也要 reject
        })

        try {
          await runStreamRequest(payloadMessages)
        }
        catch (error) {
          if (hasImageUrlMessage(payloadMessages) && isVisionUnsupportedError(error)) {
            console.warn('⚠️ 当前模型不支持图片输入，已自动降级图片内容并重试')
            nonVisionModelCache.value = markNonVisionModelCached(nonVisionModelCache.value, modelCacheKey)
            fullText = ''
            finishProcessed = false
            nativeToolsDispatched = false
            lastCallFailed.value = false
            parser = createParser()
            streamingMessage.value = { role: 'assistant', content: '', slices: [], tool_results: [] }

            await runStreamRequest(sanitizeMessagesForNonVisionModel(newMessages as Message[]))
            lastCallFailed.value = false
          }
          else if (isImageDecodeError(error)) {
            // 🧹 provider 抛出"无法解码图片 base64"，无差别降级重试：
            // 1. 清理 store 里（当前会话）的坏图（含 data URL 表情包），防止下次再踩
            // 2. payload 里所有 image_url 全部剥掉，用纯文本重试一次
            // 即便用户本次真的上传了图，至少也能拿到 AI 的文本回复，比失败强
            const cleaned = purgeInvalidImagesFromContact(targetContactKey)
            console.warn(`⚠️ provider 无法解码图片 base64，已静默清理 ${cleaned} 个坏图 part（含表情包）并降级重试`)
            const strippedMessages = stripAllImagePartsFromMessages(newMessages as Message[])
            fullText = ''
            finishProcessed = false
            nativeToolsDispatched = false
            lastCallFailed.value = false
            parser = createParser()
            streamingMessage.value = { role: 'assistant', content: '', slices: [], tool_results: [] }
            await runStreamRequest(strippedMessages)
            lastCallFailed.value = false
          }
          else {
            throw error
          }
        }

        for (const hook of onAfterSendHooks.value) {
          await hook(sendingMessage)
        }

        // 📡 AI 回复完成后再同步一次，将 assistant 消息纳入 recentMessages
        // 不传 userReplied（默认 true），保持 unansweredCount = 0，语义：对话轮次正常结束
        if (requester !== 'background_activity') {
          syncToOfflineServer({ contactId: targetContactId }).catch(() => {})
        }
      }
      catch (error) {
        lastCallFailed.value = true
        console.error('❌ 发送消息失败:', error)

        // 🔑 立即重置 sending 状态，防止 "对方输入中" 卡住
        sending.value = false

        // 📡 确保typing状态被取消（错误时）
        setAiTypingState(false)

        // 将错误信息插入聊天列表，让用户直接在对话中看到
        handleApiError(error, {
          messages: targetMessagesRef,
          notifyMessagesChanged,
          pushMessageToTarget,
        })

        throw error
      }
      finally {
        sending.value = false
        sendingContactId.value = null
        setAiTypingState(false)
      }
    }, priority, requester) // 关闭 apiQueueStore.enqueue
  }

  /**
   * 撤回指定消息（不删除，仅标记为已撤回）
   * @param timestamp 消息时间戳
   * @returns 是否撤回成功
   */
  function recallMessage(timestamp: number, contactId?: string | null): boolean {
    const targetMessages = contactId === undefined
      ? messages.value
      : (messagesByContact.value[getContactKey(contactId)] ?? [])
    const message = targetMessages.find((msg: any) => msg.timestamp === timestamp) as any
    if (!message) {
      console.warn(`⚠️ 找不到要撤回的消息 timestamp=${timestamp}`)
      return false
    }
    message.recalled = true
    message.recalledAt = Date.now()
    notifyMessagesChanged()
    console.info(`↩️ 已撤回消息 timestamp=${timestamp}, role=${message.role}`)
    return true
  }

  /**
   * 删除指定消息
   */
  function deleteMessage(timestamp: number) {
    const index = messages.value.findIndex((msg: any) => msg.timestamp === timestamp)
    if (index !== -1) {
      messages.value.splice(index, 1)
      notifyMessagesChanged()
      console.info(`🗑️ 已删除消息 timestamp=${timestamp}`)
    }
  }

  /**
   * 批量删除消息
   */
  function deleteMessages(timestamps: number[]) {
    if (timestamps.length === 0)
      return
    const toDelete = new Set(timestamps)
    messages.value = messages.value.filter((msg) => {
      if (msg.role === 'error')
        return true
      const ts = (msg as ChatMessage).timestamp
      return ts == null || !toDelete.has(ts)
    })
    notifyMessagesChanged()
    console.info(`🗑️ 已批量删除 ${timestamps.length} 条消息`)
  }

  /**
   * 编辑指定消息的内容
   */
  function editMessage(timestamp: number, newContent: string) {
    const message = messages.value.find((msg: any) => msg.timestamp === timestamp) as any
    if (message) {
      // 更新消息内容
      if (typeof message.content === 'string') {
        message.content = newContent
      }
      else if (Array.isArray(message.content)) {
        // 如果是数组格式，更新文本部分
        const textPart = message.content.find((part: any) => part.type === 'text')
        if (textPart) {
          textPart.text = newContent
        }
      }

      // 如果有 slices，也需要更新
      if (message.slices && Array.isArray(message.slices)) {
        const textSlice = message.slices.find((slice: any) => slice.type === 'text')
        if (textSlice) {
          textSlice.text = newContent
        }
      }

      notifyMessagesChanged()
      console.info(`✏️ 已编辑消息 timestamp=${timestamp}`)
    }
  }

  /**
   * 重新生成 AI 消息
   * 删除该消息及之后的所有消息，然后重新发送上一条用户消息
   */
  async function regenerateMessage(
    timestamp: number,
    options: {
      providerId: string
      model: string
      chatProvider: ChatProvider
      providerConfig?: Record<string, unknown>
    },
  ) {
    const messageIndex = messages.value.findIndex((msg: any) => msg.timestamp === timestamp)
    if (messageIndex === -1) {
      console.warn('⚠️ 找不到要重新生成的消息')
      return
    }

    // 找到该消息之前最后一条用户消息 (仅用于日志记录，不作为强制条件)
    let lastUserMessage = '（无用户消息上下文）'
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages.value[i].role === 'user') {
        const msg = messages.value[i]
        if (typeof msg.content === 'string') {
          lastUserMessage = msg.content
        }
        else if (Array.isArray(msg.content)) {
          const textPart = msg.content.find((part: any) => 'text' in part && part.type === 'text')
          lastUserMessage = textPart && 'text' in textPart ? textPart.text : ''
        }
        break
      }
    }

    // 删除该消息及之后的所有消息（"时光倒流"）
    messages.value.splice(messageIndex, messages.value.length - messageIndex)

    // 🔧 保证删除后末尾是 user 消息：继续往前删掉末尾连续的 assistant（AI 连发多条时
    // 只重生其中一条会导致末尾仍是 assistant，Anthropic 现代模型要求 messages 必须以
    // user 结尾，否则 400 "assistant-prefill final message is not supported"）。
    // 仅当历史里存在 user 消息时才修整：若整段都没有 user（如重新生成开场白），
    // 保持原样交给 isFirstInteraction 分支处理，不要删成空。
    const hasUserMsg = messages.value.some(m => m.role === 'user')
    if (hasUserMsg) {
      while (messages.value.length > 0) {
        const tail = messages.value[messages.value.length - 1]
        // system 消息发送时会被单独抽出，不影响末尾角色判断，遇到就停手
        if (tail.role === 'assistant')
          messages.value.pop()
        else
          break
      }
    }
    notifyMessagesChanged()

    console.debug(`🔄 重新生成消息，上下文: ${lastUserMessage.substring(0, 50)}...`)

    // 重新发送（跳过用户消息，因为已经在历史中了）
    await send('', {
      ...options,
      skipUserMessage: true,
    })
  }

  // 获取指定联系人的消息（供插件系统使用）
  function getMessagesForContact(contactId: string) {
    const key = getContactKey(contactId)
    ensureContactSession(key)
    return messagesByContact.value[key] || []
  }

  /**
   * 向指定联系人的对话中插入消息并持久化
   * 用于后台同步等场景，避免消息被插入到错误的联系人对话中
   */
  function pushMessageToContact(contactId: string, message: ChatMessage | ErrorMessage) {
    const key = getContactKey(contactId)
    ensureContactSession(key)
    const existing = messagesByContact.value[key]!
    existing.push(message)
    notifyMessagesChanged()
  }

  // 防竞态：记录最新同步版本号，旧请求在发送前检测到被取代则放弃
  let _syncVersion = 0

  /**
   * 静默同步当前聊天数据到离线消息后端
   * 每次 send() 完成后调用，将最新的聊天记录、记忆摘要同步到 push-server
   * 只更新上下文数据（聊天记录、记忆），API key 等配置由用户手动同步
   */
  async function syncToOfflineServer(options?: { userReplied?: boolean, contactId?: string | null }) {
    const myVersion = ++_syncVersion
    // 动态导入避免循环依赖
    const { useOfflineSyncStore } = await import('./offlineSync')
    const offlineSyncStore = useOfflineSyncStore()

    // 版本检查：如果在 await 期间有更新的同步请求，放弃本次（最新请求胜出）
    if (myVersion !== _syncVersion)
      return

    if (!offlineSyncStore.isConfigured || !offlineSyncStore.enabled)
      return

    // 在所有 await 之前快照响应式数据，防止用户切换联系人后读到错误联系人的数据
    const contactId = options?.contactId ?? activeContactId.value
    if (!contactId)
      return

    const contact = contactsStore.getContact(contactId)
    if (!contact)
      return

    // 跳过群聊，群聊不需要离线主动消息
    if (contact.isGroup)
      return

    const key = getContactKey(contactId)
    // 快照：当前联系人的消息列表
    const snapshotAllMessages = [...(messagesByContact.value[key] || [])]
    // 快照：目标联系人的增强版 systemPrompt，不能从当前激活联系人读取
    const snapshotSystemPrompt = snapshotAllMessages[0]?.role === 'system'
      ? (snapshotAllMessages[0].content as string) || null
      : null
    const targetCard = contact.cardId ? airiCardStore.getCard(contact.cardId) : undefined
    const fallbackSystemPrompt = targetCard
      ? [targetCard.systemPrompt, targetCard.description, targetCard.personality].filter(Boolean).join('\n')
      : ''
    // 快照：目标联系人的摘要，避免切换联系人后读到另一个会话的摘要
    const snapshotSummaries = memoryStore.summaries.filter(summary => summary.contactId === contactId)
    // 快照：bgStore 配置
    const bgStore = useBackgroundActivityStore()
    const providerCfg = bgStore.sendOptions?.providerConfig as Record<string, any> | undefined
    const snapshotApiKey = (providerCfg?.apiKey as string) || ''
    const snapshotBaseUrl = (providerCfg?.baseUrl as string) || ''
    const snapshotModelId = bgStore.sendOptions?.model || ''
    const snapshotBgConfig = {
      minHours: bgStore.minHours,
      maxHours: bgStore.maxHours,
      quietTimeStart: bgStore.quietTimeStart,
      quietTimeEnd: bgStore.quietTimeEnd,
      enabled: bgStore.enabled,
    }

    // 构建最近消息（最近 50 条，只取 role + content）
    const recentMessages = snapshotAllMessages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-50)
      .map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : '' }))

    // 获取记忆摘要
    const memorySummary = snapshotSummaries
      .slice(-3)
      .map(s => s.content || '')
      .filter(Boolean)
      .join('\n')

    // 获取世界书内容
    let worldBook = ''
    if (contact.worldBookIds && contact.worldBookIds.length > 0) {
      try {
        const { useWorldBookStore } = await import('./worldbook')
        const worldBookStore = useWorldBookStore()
        const entries = contact.worldBookIds
          .map(id => worldBookStore.getWorldBook(id))
          .filter(Boolean)
          .map(book => ({ keywords: book!.keywords, content: book!.content }))
        if (entries.length > 0) {
          worldBook = JSON.stringify(entries)
        }
      }
      catch { /* 世界书获取失败，忽略 */ }
    }

    const timeAware = contact.timeAwarenessEnabled !== false

    const isUserReply = options?.userReplied !== false // 默认 true（向后兼容）

    // 再次检查版本，worldbook import 也是 await 点
    if (myVersion !== _syncVersion)
      return

    try {
      await offlineSyncStore.syncConfigs([{
        contactId,
        apiKey: snapshotApiKey,
        baseUrl: snapshotBaseUrl || undefined,
        modelId: snapshotModelId || undefined,
        // 优先读快照的 messages[0]（增强版 prompt，含记忆/关系/人格注入），降级到原始卡片 prompt
        systemPrompt: snapshotSystemPrompt || fallbackSystemPrompt,
        characterName: contact.name || '角色',
        characterAvatar: await compressAvatarForPush(contact.avatar),
        recentMessages: JSON.stringify(recentMessages),
        memorySummary,
        worldBook: worldBook || undefined,
        userReplied: isUserReply,
        lastUserMessageAt: timeAware && isUserReply ? Date.now() : undefined,
        minHours: snapshotBgConfig.minHours,
        maxHours: snapshotBgConfig.maxHours,
        quietTimeStart: snapshotBgConfig.quietTimeStart,
        quietTimeEnd: snapshotBgConfig.quietTimeEnd,
        timezoneOffset: timeAware ? new Date().getTimezoneOffset() : undefined,
        enabled: snapshotBgConfig.enabled && !contactsStore.isFullyBlocked(contactId),
        backgroundMessageEnabled: contact.backgroundMessageEnabled !== false,
        isGroup: false,
      }])
    }
    catch (err) {
      // 静默失败，不影响正常聊天
      console.warn('[Chat] 同步到离线服务器失败:', err)
    }
  }

  /**
   * 合并离线消息到聊天记录
   * 将 push-server 生成的离线消息按时间戳插入到对应联系人的聊天记录中
   */
  function mergeOfflineMessages(offlineMessages: Array<{
    id: string
    contactId: string
    characterName: string
    content: string
    createdAt: number
  }>): number {
    let merged = 0

    for (const msg of offlineMessages) {
      const contact = contactsStore.getContact(msg.contactId)
      if (contact?.isGroup) {
        console.info(`👥 跳过群聊离线主动消息: ${contact.name}`)
        continue
      }

      // E1: 跨设备/迟到消息丢弃 —— push-server 调度可能在拉黑生效前已生成消息，
      // 这些消息在拉黑后不应进入聊天记录，否则会破坏 banner 状态和"安静"语义
      if (contactsStore.isFullyBlocked(msg.contactId)) {
        console.info(`🚫 跳过被拉黑联系人的离线消息: ${contact?.name ?? msg.contactId}`)
        continue
      }

      const key = getContactKey(msg.contactId)
      ensureContactSession(key)
      const existing = messagesByContact.value[key] || []

      // 去重：检查是否已存在相同 offlineMessageId 的消息
      const isDuplicate = existing.some((m) => {
        if (m.role !== 'assistant')
          return false
        const am = m as ChatAssistantMessage
        return am.timestamp === msg.createdAt && am.content === msg.content
      })
      if (isDuplicate)
        continue

      // 构建 ChatAssistantMessage
      const chatMsg: ChatAssistantMessage = {
        role: 'assistant',
        content: msg.content,
        slices: [{ type: 'text', text: msg.content }],
        tool_results: [],
        timestamp: msg.createdAt,
        senderName: msg.characterName,
      }

      // 按时间戳插入到正确位置
      let insertIdx = existing.length
      for (let i = existing.length - 1; i >= 0; i--) {
        const ts = (existing[i] as { timestamp?: number }).timestamp
        if (ts && ts <= msg.createdAt) {
          insertIdx = i + 1
          break
        }
        if (i === 0) {
          // 所有消息都比离线消息新，插入到 system message 之后
          insertIdx = existing[0]?.role === 'system' ? 1 : 0
        }
      }

      existing.splice(insertIdx, 0, chatMsg)
      merged++
    }

    if (merged > 0) {
      notifyMessagesChanged()
    }

    return merged
  }

  return {
    sending,
    sendingContactId,
    messages,
    notifyMessagesChanged,
    streamingMessage,
    transientNotices,
    clearTransientNoticeTimers,
    conversationTurns,
    lastMessageTime,
    notificationPermission,
    notificationEnabled,

    discoverToolsCompatibility,

    getMessagesForContact,
    pushMessageToContact,
    exportChatData,
    importChatData,
    mergeOfflineMessages,
    syncToOfflineServer,
    send,
    sendUserMessage,
    triggerAIReplyWithDebounce,
    cancelAIReplyDebounce,
    executeAutoReply,
    cleanupMessages,
    purgeApplicationPromptMessages,
    deleteContactChatData,
    clearHooks,
    requestNotificationPermission,
    sendDesktopNotification,
    recallMessage,
    deleteMessage,
    deleteMessages,
    editMessage,
    regenerateMessage,
    saveMessages: messagesByContactStorage.save,

    onBeforeMessageComposed,
    onAfterMessageComposed,
    onBeforeSend,
    onAfterSend,
    onTokenLiteral,
    onTokenSpecial,
    onStreamEnd,
    onAssistantResponseEnd,
    onFullTextGenerated,
    triggerFullTextSpeech,
    setAiTypingState,
  }
})
